/* ────────────────────────────────────────────────────────────────
 *  ReportIncidentPanel.jsx – full file (updated clean UI)
 * ──────────────────────────────────────────────────────────────── */
 import React, {
  useState, useEffect, useContext, useCallback, useRef, useMemo,
} from 'react';

import {
  Card, Container, Row, Col, ListGroup, Button, Modal, Spinner, Form, Alert,
} from 'react-bootstrap';
import AlertTypeCategorySelector from './AlertTypeCategorySelector';

import { useNavigate } from 'react-router-dom';
import { ExclamationTriangleFill } from 'react-bootstrap-icons';

import DashboardCardHeader               from '../ViewReports/DashboardCardHeader';
import DropdownSearchInfrastructureAlert from './DropdownSearchInfrastructureAlert';
import IncidentTypeModal                 from './IncidentTypeModal';
import ReportStatusModal                 from './ReportStatusModal';
import GroupsModal                       from './GroupsModal';
import MessageMediaModal                 from './MessageMediaModal';
import SendStatusModal                   from './SendStatusModal';
import NoPrimaryCampusModal              from './NoPrimaryCampusModal';
import logo                              from '../ViewReports/SchoolShield_color300x3xx.webp';

import { UserContext } from '../../userAlertContext';
import axios           from '../axiosAlertConfig';
import panelStyles     from './ReportIncidentPanel.module.css';

/* ───────── helpers ───────── */
const DBG = false;
const dbg = (m,o)=>DBG&&console.log('[ReportIncidentPanel]',m,o??'');
const up   = v => (v ?? '').toString().toUpperCase();

const uniqStrings = (values = []) =>
  [...new Set((values || []).map((v) => String(v)).filter(Boolean))];

// Canonicalize human labels to a stable key
const canonicalTypeKey = (t) => {
 const k = (t ?? '').toLowerCase();
 if (k.includes('lock'))    return 'lockdown';
 if (k.includes('evac'))    return 'evacuate';
 if (k.includes('secure'))  return 'secure';
 if (k.includes('shelter')) return 'shelter';
 if (k.includes('hold'))    return 'hold';
  return k.trim();
};

const colour = (t) => {
  const k = (t ?? '').toLowerCase();
  if (k.includes('lock'))    return 'danger';
  if (k.includes('evac'))    return 'warning';
  if (k.includes('secure'))  return 'primary';
  if (k.includes('shelter')) return 'info';
  if (k.includes('hold'))    return 'secondary';
  return 'dark';
};

const API = '/ialert/incidents';


/* scoped caches so Show All doesn’t pollute default view
   and non-admins are campus-scoped */
const CACHE_ALL    = 'incidentsCache:all';
const CACHE_RECENT = 'incidentsCache:active24h';
const cacheKeyFor  = (useAll, campusId, isPrivileged) =>
  useAll
    ? CACHE_ALL
    : (isPrivileged ? CACHE_RECENT : `${CACHE_RECENT}:campus:${campusId || 'none'}`);

/* ─── MAIN COMPONENT ──────────────────────────────────────────── */
export default function ReportIncidentPanel() {
  const navigate = useNavigate();
  const {
    user, iBuildingID, buildingName, isTestIncident, /* decoupled: setIsTestIncident */
  } = useContext(UserContext);

  const autoOpenDoneRef = useRef(false);


  // Default category to use when the Verify (Type/Category) modal opens with no category chosen yet
const DEFAULT_CATEGORY_BY_TYPE = {
  lockdown: 'Active Shooter',
};

const defaultCategoryForType = (type) =>
  DEFAULT_CATEGORY_BY_TYPE[canonicalTypeKey(type)] || '';


  // ─── Smooth navigation guard (TTL-based) ──────────────────────────
  const AUTO_SUPPRESS_MS = 3000;
  const shouldSuppressAutoOpen = () => {
    const now = Date.now();
    const until = Number(sessionStorage.getItem('suppressAutoOpenUntil') || 0);
    if (until > now) return true;
    const activeOpenAt = Number(sessionStorage.getItem('activeOpenAt') || 0);
    if (activeOpenAt && (now - activeOpenAt) < AUTO_SUPPRESS_MS) return true;
    return false;
  };

  useEffect(() => {
    if (sessionStorage.getItem('skipAutoOpenOnce') === '1') {
      sessionStorage.removeItem('skipAutoOpenOnce');
      sessionStorage.setItem('suppressAutoOpenUntil', String(Date.now() + AUTO_SUPPRESS_MS));
    }
    if (shouldSuppressAutoOpen()) autoOpenDoneRef.current = true;
  }, []);

  // ── Local drill mode only (no global sync) ────────────────────────
  const [modeIsDrill, setModeIsDrill] = useState(() => {
    if (localStorage.getItem('isTestIncidentOverride') === '1') return true;
    const v = sessionStorage.getItem('panelModeIsDrill');
    return v !== null ? v === '1' : !!isTestIncident;
  });

  const [modeIsSilent, setModeIsSilent] = useState(() => {
    const v = sessionStorage.getItem('panelModeIsSilent');
    return v !== null ? v === '1' : false;   // default = LOUD
  });


  //////////////////////////////////////////////////////////////////
  // 911 modal state & helpers
  const [showCall911, setShowCall911] = useState(false);
  const [pendingIncident, setPendingIncident] = useState(null);
  const [dialProgress, setDialProgress] = useState(0);
  const [deferOpen911, setDeferOpen911] = useState(false);

  const open911Modal = (incident) => {
    setPendingIncident(incident);
    setDialProgress(0);
    setShowCall911(true);
  };

  const navigateToPendingIncident = () => {
    const inc = pendingIncident;
    setShowCall911(false);
    setPendingIncident(null);
    if (inc?.incidentID) {
      navigate(`/activeincidentpage/${inc.incidentID}`, { state: { incident: inc } });
    }
  };

  const handleDial911 = () => {
    try {
      window.location.href = 'tel:911';
    } finally {
      navigateToPendingIncident();
    }
  };

  const handleSliderRelease = () => {
    if (dialProgress >= 95) {
      handleDial911();
    } else {
      setDialProgress(0);
    }
  };

  // ─── time helpers ───────────────────────────────
  const MS_HOUR = 3600000;
  const MS_DAY  = 24 * MS_HOUR;
  const fmtTS = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit'
    });
  };
  const ageFromNow = (iso) => {
    if (!iso) return { within24:false, rel:'' };
    const ms = Date.now() - new Date(iso).getTime();
    const within24 = ms <= MS_DAY;
    const h = Math.floor(ms / MS_HOUR);
    const m = Math.floor((ms % MS_HOUR) / 60000);
    return { within24, rel: `${h}h ${m}m` };
  };

  // Persist local drill mode only
  useEffect(() => {
    sessionStorage.setItem('panelModeIsDrill', modeIsDrill ? '1' : '0');
  }, [modeIsDrill]);

  useEffect(() => {
    sessionStorage.setItem('panelModeIsSilent', modeIsSilent ? '1' : '0');
  }, [modeIsSilent]);


  const handleMissingPrimaryCampusClose = useCallback(() => {
    sessionStorage.removeItem('defaultCampusID');
    sessionStorage.removeItem('defaultCampusName');
    sessionStorage.removeItem('skipAutoOpenOnce');
    sessionStorage.removeItem('suppressAutoOpenUntil');
    navigate('/login', { replace: true });
  }, [navigate]);

 const handleLogout = useCallback(() => {
  sessionStorage.removeItem('defaultCampusID');
  sessionStorage.removeItem('defaultCampusName');
  sessionStorage.removeItem('skipAutoOpenOnce');
  sessionStorage.removeItem('suppressAutoOpenUntil');
  sessionStorage.removeItem('panelModeIsDrill');
  sessionStorage.removeItem('panelModeIsSilent');
  sessionStorage.removeItem('incidentsShowAll');
  sessionStorage.removeItem('incidentsCache');
  sessionStorage.removeItem(CACHE_ALL);
  sessionStorage.removeItem(CACHE_RECENT);
  sessionStorage.removeItem('activeOpenAt');

  for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith(`${CACHE_RECENT}:campus:`)) {
      sessionStorage.removeItem(key);
    }
  }

  localStorage.removeItem('isTestIncidentOverride');

  navigate('/login', { replace: true });
}, [navigate]);


  /* ── load BuildingInfos once ── */
   /* ── load BuildingInfos once ── */
  const [buildingInfos, setBuildingInfos] = useState([]);
  const [campusGroups, setCampusGroups] = useState({});

  useEffect(() => {
    if (!user?.iAccountID) return;

    axios.get('/buildinginfos', { params: { iAccountID: user.iAccountID } })
      .then(({ data }) => setBuildingInfos(data || []))
      .catch((e) => {
        console.error('load buildings', e);
        setBuildingInfos([]);
      });
  }, [user?.iAccountID]);

  useEffect(() => {
    if (!user?.iAccountID) {
      setCampusGroups({});
      return;
    }

    axios.get('/CampusBuildings', { params: { iAccountID: user.iAccountID } })
      .then(({ data }) => {
        const rawGroups = data?.groups || {};
        const normalized = {};

        Object.keys(rawGroups).forEach((campusId) => {
          normalized[String(campusId)] = uniqStrings(
            Array.isArray(rawGroups[campusId]) ? rawGroups[campusId] : []
          );
        });

        setCampusGroups(normalized);
      })
      .catch((e) => {
        console.error('load campus groups', e);
        setCampusGroups({});
      });
  }, [user?.iAccountID]);

  const mainCampusIdByBuildingId = useMemo(() => {
    const out = {};

    Object.keys(campusGroups || {}).forEach((campusId) => {
      const sid = String(campusId);
      out[sid] = sid;

      uniqStrings(campusGroups[campusId] || []).forEach((buildingId) => {
        out[String(buildingId)] = sid;
      });
    });

    return out;
  }, [campusGroups]);

  const mainCampusIdSet = useMemo(
    () => new Set(Object.keys(campusGroups || {}).map(String)),
    [campusGroups]
  );

  const normalizeIDsToMainCampuses = useCallback((ids = []) => {
    const raw = uniqStrings(ids);

    if (!raw.length) return [];
    if (!mainCampusIdSet.size) return raw;

    return uniqStrings(
      raw
        .map((id) => mainCampusIdByBuildingId[String(id)] || String(id))
        .filter((id) => mainCampusIdSet.has(String(id)))
    );
  }, [mainCampusIdByBuildingId, mainCampusIdSet]);

  const mainCampusBuildingInfos = useMemo(() => {
    if (!Array.isArray(buildingInfos) || !buildingInfos.length) return [];
    if (!mainCampusIdSet.size) return [];

    const arr = buildingInfos.filter((b) =>
      mainCampusIdSet.has(String(b.iBuildingID))
    );

    arr.sort((a, b) =>
      String(a?.BuildingName || '').localeCompare(
        String(b?.BuildingName || ''),
        undefined,
        { sensitivity: 'base' }
      )
    );

    return arr;
  }, [buildingInfos, mainCampusIdSet]);


const normalizedUserCampusIDs = useMemo(
  () => normalizeIDsToMainCampuses(user?.campusIDs || []),
  [user?.campusIDs, normalizeIDsToMainCampuses]
);

const assignedCampusBuildingInfos = useMemo(() => {
  const assignedSet = new Set((normalizedUserCampusIDs || []).map(String));

  const arr = (buildingInfos || []).filter((b) =>
    assignedSet.has(String(b.iBuildingID))
  );

  arr.sort((a, b) =>
    String(a?.BuildingName || '').localeCompare(
      String(b?.BuildingName || ''),
      undefined,
      { sensitivity: 'base' }
    )
  );

  return arr;
}, [buildingInfos, normalizedUserCampusIDs]);




  const primaryCampusId = normalizedUserCampusIDs[0] ?? '';

  const resolvedPrimaryCampusName = useMemo(() => {
    return (
      buildingInfos.find((b) => String(b.iBuildingID) === String(primaryCampusId))?.BuildingName ||
      user?.campusNames?.[0] ||
      ''
    );
  }, [buildingInfos, primaryCampusId, user?.campusNames]);

  /**
   * Auto-open only if ACTIVE incident in last 24h for user's primary campus.
   */
  const autoOpenLatestIfAny = useCallback(async () => {
    if (autoOpenDoneRef.current) return;
    if (shouldSuppressAutoOpen()) { autoOpenDoneRef.current = true; return; }



    const acctID   = user?.iAccountID;
    const campusID = primaryCampusId;
    if (!acctID || !campusID) return;

    try {
      const { data } = await axios.get(API, {
        params: {
          iAccountID : acctID,
          iCampusID  : campusID,
          iBuildingID: '-99',
          activeOnly : '1',
          hours      : 24,
        },
      });

      const list = Array.isArray(data) ? data.slice() : [];
      if (!list.length) { autoOpenDoneRef.current = true; return; }

      const now = Date.now();
      const within24Active = list.filter(r =>
        Number(r.isActive) === 1 &&
        (now - new Date(r.createdAt).getTime()) <= MS_DAY
      );

      if (!within24Active.length) { autoOpenDoneRef.current = true; return; }

      within24Active.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      const target = within24Active[0];

      sessionStorage.setItem('skipAutoOpenOnce', '1');
      autoOpenDoneRef.current = true;

      navigate(`/activeincidentpage/${target.incidentID}`, {
        state: { incident: target },
        replace: true
      });
    } catch (e) {
      console.error('[auto-open] failed', e);
    }
   }, [user?.iAccountID, primaryCampusId, navigate]);

  useEffect(() => { autoOpenLatestIfAny(); }, [autoOpenLatestIfAny]);

  /* ── campus selection state (default = Users JSON [0]) ── */
   /* ── campus selection state (default = normalized main campus) ── */
  const initID = primaryCampusId || '';
  const initName = resolvedPrimaryCampusName || user?.campusNames?.[0] || '';

  // show warning when user has more than one assigned main campus
  const hasMultipleAlertCampuses = normalizedUserCampusIDs.length > 1;

  const roleLower    = (user?.userRole || '').toLowerCase();
  const isAdmin      = ['administrator','systemadministrator'].includes(roleLower);
  const isPrivileged = isAdmin;

  const [selID, setSelID] = useState(
    () => sessionStorage.getItem('defaultCampusID') || initID
  );
  const [selName, setSelName] = useState(
    () => sessionStorage.getItem('defaultCampusName') || initName
  );

  const normalizeCampusSelection = useCallback((rawId, rawName = '') => {
    const ids = String(rawId || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    if (!ids.length) {
      return { id: '', name: '', ids: [] };
    }

    if (!mainCampusIdSet.size) {
      return {
        id: ids.join(','),
        name: String(rawName || ''),
        ids,
      };
    }

    const normalizedIds = normalizeIDsToMainCampuses(ids);
    const normalizedNames = normalizedIds
      .map((id) =>
        buildingInfos.find((b) => String(b.iBuildingID) === String(id))?.BuildingName || ''
      )
      .filter(Boolean);

    return {
      id: normalizedIds.join(','),
      name: normalizedNames.join(', '),
      ids: normalizedIds,
    };
  }, [mainCampusIdSet, normalizeIDsToMainCampuses, buildingInfos]);


 useEffect(() => {
  const assignedIds = new Set((normalizedUserCampusIDs || []).map(String));

  const currentIds = String(selID || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const currentIsAssigned =
    currentIds.length > 0 &&
    currentIds.every((id) => assignedIds.has(String(id)));

  const id = primaryCampusId;
  const name = resolvedPrimaryCampusName;

  if ((!selID || !currentIsAssigned) && id) {
    setSelID(id);
    setSelName(name || 'Campus');
    sessionStorage.setItem('defaultCampusID', id);
    sessionStorage.setItem('defaultCampusName', name || 'Campus');
  }
}, [
  selID,
  primaryCampusId,
  resolvedPrimaryCampusName,
  normalizedUserCampusIDs,
]);

  useEffect(() => {
    if (!selID || !mainCampusIdSet.size) return;

    const normalized = normalizeCampusSelection(selID, selName);
    const nextId = normalized.id || selID;
    const nextName = normalized.name || selName;

    if (nextId !== selID || nextName !== selName) {
      setSelID(nextId);
      setSelName(nextName);
      sessionStorage.setItem('defaultCampusID', nextId);
      sessionStorage.setItem('defaultCampusName', nextName);
    }
  }, [selID, selName, mainCampusIdSet, normalizeCampusSelection]);

  const handleSelectCampus = ({ id, name }) => {
    const normalized = normalizeCampusSelection(id, name);
    const nextId = normalized.id || String(id || '');
    const nextName = normalized.name || String(name || '');

    dbg('dropdown → onSelect', { id: nextId, name: nextName });

    setSelID(nextId);
    setSelName(nextName);
    sessionStorage.setItem('defaultCampusID', nextId);
    sessionStorage.setItem('defaultCampusName', nextName);
  };






  /* ── incidents cache / refresh logic ── */
  const fullName   = [user?.firstName,user?.lastName].filter(Boolean).join(' ');
  const acctID     = user?.iAccountID ?? '';
  const uid        = user?.userID     ?? 0;
  const safeCID    = selID   || '-99';
  const safeCName  = selName || '';
  const safeBID    = iBuildingID?.trim() ? iBuildingID : '-99';

  const safeBldgID     = iBuildingID?.trim() ? iBuildingID : '-99';
  const safeCampusID   = safeCID || '-99';
  const safeCampusName = selName || '';
  const safeAcctID     = user?.iAccountID  ?? '';
  const safeUID        = user?.userID      ?? 0;
  const safeFName      = user?.firstName   ?? '';
  const safeLName      = user?.lastName    ?? '';

  const specialStudentScope = useMemo(() => {
  const selectedIds = String(selID || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

  const resolvedBuildingId =
    selectedIds[0] ||
    primaryCampusId ||
    safeBID ||
    safeBldgID ||
    '-99';

  return {
    iAccountID: String(safeAcctID || '').trim(),
    iCampusID: '-99',
    iBuildingID: String(resolvedBuildingId || '').trim(),
  };
}, [selID, primaryCampusId, safeAcctID, safeBID, safeBldgID]);


  //const primaryCampusId = user?.campusIDs?.[0] ?? '';

  const [posting,setPosting] = useState(false);

  // default: Show All OFF (non-admins are always 24h)
  const [showAll, setShowAll] = useState(() =>
    (isAdmin && sessionStorage.getItem('incidentsShowAll') === '1')
  );

  const effectiveShowAll = isAdmin ? showAll : false;

  /** Build authorized target building IDs from current selection. */
   /** Build authorized target main campus IDs from current selection. */
  const getAuthorizedTargetIDs = React.useCallback(() => {
    const rawSel = selID || '';



 const userPrimaryIDs = normalizeIDsToMainCampuses(user?.campusIDs || []);


    let selected = [];
 if (rawSel === '-99') {
  selected = userPrimaryIDs;
    } else if (rawSel.includes(',')) {
      selected = normalizeIDsToMainCampuses(
        rawSel.split(',').map((s) => s.trim()).filter(Boolean)
      );
    } else if (rawSel) {
      selected = normalizeIDsToMainCampuses([rawSel]);
    }

const authorized = selected.filter((id) =>
  userPrimaryIDs.includes(String(id))
);

    return uniqStrings(authorized);
 }, [selID, user?.campusIDs, normalizeIDsToMainCampuses]);

  /* 🆕 Uniform relaxed policy for all campuses:
     Block ONLY if there is an ACTIVE same‑type incident in <24h for this campus.
     Different types never block. Closed same‑type <24h does not block.
  */
  const findRecentIncidentByType = useCallback(async (type, campusId) => {
    if (!safeAcctID || !campusId) return null;
    try {
      const { data } = await axios.get(API, {
        params: {
          iAccountID : safeAcctID,
          iCampusID  : campusId,
          iBuildingID: '-99',
          activeOnly : '0',
          hours      : 24,
        },
      });
      const list = Array.isArray(data) ? data : [];
      const keyWanted = canonicalTypeKey(type);
      const matches = list.filter(r => canonicalTypeKey(r.incidentType) === keyWanted);
      if (!matches.length) return null;

      // Uniform relaxed rule for everyone:
      const activeSameType = matches.find(r => Number(r.isActive) === 1);
      return activeSameType || null;
    } catch (e) {
      console.error('dup-check failed', e);
      return null;
    }
  }, [safeAcctID]);

  // if role changes to non-admin, force 24h + persist
  useEffect(() => {
    if (!isAdmin && showAll) {
      setShowAll(false);
      sessionStorage.setItem('incidentsShowAll', '0');
    }
  }, [isAdmin, showAll]);

  // hydrate incidents from the correct scoped cache (campus-scoped for non-admins)
   const [incidents,setIncidents] = useState(() => {
    try {
      const useAllAtInit = (sessionStorage.getItem('incidentsShowAll') === '1');
      const key = cacheKeyFor(isAdmin && useAllAtInit, (primaryCampusId || ''), isAdmin);
      return JSON.parse(sessionStorage.getItem(key) || '[]');
    } catch { return []; }
  });

  // LOAD: role-gated list scope
  const loadIncidents = useCallback(async () => {
    if (!acctID) return;

    const useAll = effectiveShowAll;
    const campusForList = isAdmin ? '-99' : (primaryCampusId || '');
    if (!isAdmin && !campusForList) return;

    try {
      const params = {
        iAccountID:  acctID,
        iCampusID:   campusForList,
        iBuildingID: '-99',
        activeOnly:  useAll ? '0' : '1',
        ...(useAll ? {} : { hours: 24 }),
      };
      const { data } = await axios.get(API, { params });
      setIncidents(data);
      sessionStorage.setItem(cacheKeyFor(useAll, campusForList, isAdmin), JSON.stringify(data));
      sessionStorage.setItem('incidentsCache', JSON.stringify(data)); // legacy
    } catch (e) {
      console.error('fetch incidents', e);
    }
  }, [acctID, effectiveShowAll, isAdmin, primaryCampusId]);

  useEffect(() => {
    sessionStorage.setItem('incidentsShowAll', effectiveShowAll ? '1' : '0');
    try {
      const campusForList = isAdmin ? '-99' : (primaryCampusId || '');
      const cached = JSON.parse(sessionStorage.getItem(cacheKeyFor(effectiveShowAll, campusForList, isAdmin)) || '[]');
      if (Array.isArray(cached)) setIncidents(cached);
    } catch {}
  }, [effectiveShowAll, isAdmin, primaryCampusId]);

  useEffect(() => { if (!posting) loadIncidents(); }, [posting, effectiveShowAll, loadIncidents]);

  /* ------------------------------------------------------------------ *
   *  sendAlert() – create the incident, then FAN-OUT via /dispatch
   * ------------------------------------------------------------------ */
  const [isPosting, setIsPosting] = useState(false);

  const sendAlert = async () => {
    if (!incidentType) { alert('Select an incident type first'); return; }

    const targetIDs = getAuthorizedTargetIDs();
    if (!targetIDs.length) { alert('No authorized buildings selected.'); return; }

    const primaryCampusIdLocal = targetIDs[0];
    const primaryCampusName =
      (buildingInfos.find(b => String(b.iBuildingID) === String(primaryCampusIdLocal))?.BuildingName) ||
      (user?.campusNames?.[0] ?? safeCampusName);

    // 🆕 Uniform campus‑scoped duplicate gate
    const dup = await findRecentIncidentByType(incidentType, primaryCampusIdLocal);
    if (dup) {
      const label = canonicalTypeKey(incidentType);
      alert(`An ACTIVE ${label} incident already exists for ${primaryCampusName}. Opening it instead.`);
      navigate(`/activeincidentpage/${dup.incidentID}`, { state: { incident: dup } });
      return;
    }

    const modeNow = modeIsDrill ? 'DRILL' : 'REAL';

    try {
      setIsPosting(true);
      const { data } = await axios.post(API, {
        iAccountID  : safeAcctID,
        iCampusID   : primaryCampusIdLocal,
        iBuildingID : primaryCampusIdLocal,
        buildingName: primaryCampusName,
        incidentType,
        incidentCategory,
        incidentMode : modeNow,
        createdByUserID      : safeUID,
        createdByName        : fullName,
        createdByMobileNumber: user?.UserPhoneNumber || '',
        iUserID     : safeUID,
        userName    : user?.userName || user?.username || '',
        myStatus      : statusArr,
        sendToGroups  : groupsArr,
        messageText   : messageText,
        attachmentType: attachment?.type ?? null,
        attachmentURL : attachment?.url  ?? null,
      });

      const newIncident = data.incident || data;


//console.log('[REPORTINCIDENTPANEL-1] ', newIncident)


      // build shared values once
      const campusList = targetIDs
        .map(id => buildingInfos.find(b => String(b.iBuildingID) === String(id))?.BuildingName)
        .filter(Boolean)
        .join(', ');

      const isDrill = String(modeNow).toLowerCase() === 'drill';

      // MESSAGE TEXT ---------------------------------------------------------
      const messageBody = `This is a ${modeNow} ${incidentType} at ${campusList}`;
      const finalMessageText = isDrill
        ? `DRILL DRILL DRILL ${messageBody} DRILL DRILL DRILL`
        : messageBody;

      // VOICE TEXT -----------------------------------------------------------
      const voiceBase = `This is a ${modeNow} SchoolShield ${incidentType} alert at ${campusList}.`;
      const voiceSegment = isDrill
        ? `DRILL DRILL DRILL ${voiceBase} DRILL`
        : voiceBase;
      const finalVoiceText = [voiceSegment, voiceSegment, voiceSegment].join('\n');

      // DISPATCH -------------------------------------------------------------
       axios.post('/ialert/dispatch', {
        incidentID: newIncident.incidentID,
        iAccountID: safeAcctID,
        iBuildingIDs: targetIDs,
        requestedByUserID: safeUID,
        isSystemAdmin: isPrivileged,
        incidentMode: modeNow,
        silentMode: modeIsSilent ? 1 : 0,   // NEW
        subject: `${modeNow} SchoolShield Alert – ${incidentType} *${modeNow}*`,
        messageText: finalMessageText,
        voiceText: finalVoiceText
      }).catch(e => console.error('dispatch fail', e));

      if (incidentType.toLowerCase().includes('test')) {
        axios.post('/ialert/testmode/start', {
          iAccountID: safeAcctID,
          iCampusID: primaryCampusIdLocal,
          iBuildingID: primaryCampusIdLocal,
          startedByUserID: safeUID,
          startedByFirstName: safeFName,
          startedByLastName: safeLName,
        }).catch(e => console.error('auto-start test-mode', e));
      }
    } catch (err) {
      console.error('create incident', err);
      alert('Server error');
    } finally {
      setIsPosting(false);
      await loadIncidents();
      setIncidentType(''); setIncidentCategory('');
      setStatusArr([]);   setGroupsArr(['Staff Roster']);
      setMessageText(''); setAttachment(null);
      setShowSendModal(false);
    }
  };

  /* ── local modal state  ── */
  const [incidentType,setIncidentType] = useState('Lockdown');
  const [incidentCategory,setIncidentCategory] = useState('');
  const [statusArr,setStatusArr] = useState([]);
  const [groupsArr,setGroupsArr] = useState(['Staff Roster']);
  const [messageText,setMessageText] = useState('');
  const [attachment,setAttachment] = useState(null);

   const [showType,setShowType] = useState(false);
const [showStatus,setShowStatus] = useState(false);
const [showGroups,setShowGroups] = useState(false);
const [showMsg,setShowMsg] = useState(false);
const [showSendModal,setShowSendModal] = useState(false);
const [showTypeCatModal, setShowTypeCatModal] = useState(false);

  
  // 🆕 Flag to style the Type/Category modal only when launched by 911
  const [isVerifyContext, setIsVerifyContext] = useState(false);

// Force AlertTypeCategorySelector to remount each time the modal opens
// (prevents intermittent defaulting when the selector uses internal state/defaultValue)
const [typeCatModalKey, setTypeCatModalKey] = useState(0);

// Open verify modal + guarantee deterministic default category when empty
const openVerifyTypeCategoryModal = useCallback(() => {
  const def = defaultCategoryForType(incidentType);
  if (def) {
    setIncidentCategory((prev) => (String(prev || '').trim() ? prev : def));
  }
  setIsVerifyContext(true);
  setTypeCatModalKey((k) => k + 1);
  setShowTypeCatModal(true);
}, [incidentType]);





  // NEW: Help modal state
  const [showHelp, setShowHelp] = useState(false);

  // Missing primary campus modal state
  const [showMissingPrimaryCampus, setShowMissingPrimaryCampus] = useState(false);

// NEW: dropdown tells the panel the user has no primary campus
const handleMissingPrimaryCampusDetected = useCallback(() => {
  setSelID('');
  setSelName('');
  setShowMissingPrimaryCampus(true);
}, []);


  // NEW: create incident, dispatch, and return the created record
  const createIncidentAndDispatch = async () => {
    const modeNow = modeIsDrill ? 'DRILL' : 'REAL';

    const targetIDs = getAuthorizedTargetIDs();
    if (!targetIDs.length) { alert('No authorized buildings selected.'); return null; }
    const primaryCampusIdLocal = targetIDs[0];
    const primaryCampusName =
      (buildingInfos.find(b => String(b.iBuildingID) === String(primaryCampusIdLocal))?.BuildingName) ||
      (user?.campusNames?.[0] ?? safeCampusName);

    // 🆕 Uniform campus‑scoped duplicate gate
    const dup = await findRecentIncidentByType(incidentType, primaryCampusIdLocal);
    if (dup) {
      return {
        ...dup,
        __existing: true,
        __existingReason: 'active-duplicate',
        __campusName: primaryCampusName,
      };
    }

    try {
      setIsPosting(true);
      const { data } = await axios.post(API, {
        iAccountID  : safeAcctID,
        iCampusID   : primaryCampusIdLocal,
        iBuildingID : primaryCampusIdLocal,
        buildingName: primaryCampusName,
        incidentType,
        incidentCategory,
        incidentMode : modeNow,
        createdByUserID      : safeUID,
        createdByName        : fullName,
        createdByMobileNumber: user?.UserPhoneNumber || '',
        iUserID     : safeUID,
        userName    : user?.userName || user?.username || '',
        myStatus      : statusArr,
        sendToGroups  : groupsArr,
        messageText   : messageText,
        attachmentType: attachment?.type ?? null,
        attachmentURL : attachment?.url  ?? null,
      });

      const newIncident = data.incident || data;

 axios.post('/ialert/dispatch', {
  incidentID : newIncident.incidentID,
  iAccountID : safeAcctID,
  iBuildingIDs: targetIDs,
  requestedByUserID: safeUID,
  isSystemAdmin: isPrivileged,
  incidentMode: modeNow,
  silentMode: modeIsSilent ? 1 : 0,   // NEW
  subject: `${modeNow} SchoolShield Alert – ${incidentType} *${modeNow}*`,

  messageText: `This is a ${modeNow} ${incidentType} at ${
    targetIDs
      .map(id => buildingInfos.find(b => String(b.iBuildingID) === String(id))?.BuildingName)
      .filter(Boolean).join(', ')
  }`,

  voiceText: `This is a ${modeNow} ${incidentType} at ${
    targetIDs
      .map(id => buildingInfos.find(b => String(b.iBuildingID) === String(id))?.BuildingName)
      .filter(Boolean).join(', ')
  } emergency alert. Please check your device immediately.`
}).catch(e => console.error('dispatch fail', e));


      if ((incidentType || '').toLowerCase().includes('test')) {
        axios.post('/ialert/testmode/start', {
          iAccountID: safeAcctID,
          iCampusID: primaryCampusIdLocal,
          iBuildingID: primaryCampusIdLocal,
          startedByUserID: safeUID,
          startedByFirstName: safeFName,
          startedByLastName : safeLName,
        }).catch(e => console.error('auto-start test-mode', e));
      }

      return newIncident;
    } finally {
      setIsPosting(false);
    }
  };

  const handleCategoryForModal = async (cat) => {
    setIncidentCategory(cat);
    if ((cat || '').trim().toLowerCase() === 'user selection') {
      try {
        const newIncident = await createIncidentAndDispatch();

        if (newIncident?.__existing) {
          await loadIncidents();
          setPendingIncident(null);
          setDeferOpen911(false);
          setShowTypeCatModal(false);

          const label  = canonicalTypeKey(incidentType);
          const campus = newIncident.__campusName || safeCampusName;

          alert(`An ACTIVE ${label} incident already exists for ${campus}. Opening it.`);
          navigate(`/activeincidentpage/${newIncident.incidentID}`, { state: { incident: newIncident } });
          return;
        }

        if (newIncident?.incidentID) {
          await loadIncidents();
          setPendingIncident(newIncident);
          setDeferOpen911(true);
        }
      } catch (e) {
        console.error('auto-create/open from selector', e);
        alert('Failed to create the incident.');
      } finally {
        setStatusArr([]);   setGroupsArr(['Staff Roster']);
        setMessageText(''); setAttachment(null);
        setShowTypeCatModal(false);
      }
    }
  };

  /* ── role-gated visible list (client-side belt & suspenders) ── */
  const visibleIncidents = React.useMemo(() => {
    if (isAdmin) return incidents;
    const pid = String(primaryCampusId || '');
    return incidents.filter(x =>
      String(x.iCampusID ?? '') === pid || String(x.iBuildingID ?? '') === pid
    );
  }, [incidents, isAdmin, primaryCampusId]);

  // Map of buildingID -> BuildingName for quick lookup
  const buildingNameMap = React.useMemo(() => {
    const m = {};
    (buildingInfos || []).forEach(b => { m[String(b.iBuildingID)] = b.BuildingName; });
    return m;
  }, [buildingInfos]);

 
  /* ── incident row renderer (type + category + location pill + timestamp) ── */
const rows = visibleIncidents.map(x => {
  const varnt    = colour(x.incidentType);
  const catLabel = x.incidentCategory ? `– ${x.incidentCategory}` : '';
  const drill    = (x.incidentMode ?? '').toUpperCase() === 'DRILL';
  const age      = ageFromNow(x.createdAt);
  const ts       = fmtTS(x.createdAt);

  // Resolve campus/primary location name robustly
  const campusName =
    x.buildingName ||
    x.campusName ||
    buildingNameMap[String(x.iBuildingID ?? '')] ||
    buildingNameMap[String(x.iCampusID ?? '')] ||
    '';

  return (
    <ListGroup.Item
      key={x.incidentID}
      className="p-1 mb-1"
      role="button"
      tabIndex={0}
      onClick={() => {
        sessionStorage.setItem(
          'suppressAutoOpenUntil',
          String(Date.now() + AUTO_SUPPRESS_MS)
        );
        navigate(`/activeincidentpage/${x.incidentID}`, { state: { incident: x } });
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          sessionStorage.setItem(
            'suppressAutoOpenUntil',
            String(Date.now() + AUTO_SUPPRESS_MS)
          );
          navigate(`/activeincidentpage/${x.incidentID}`, { state: { incident: x } });
        }
      }}
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0,1fr) auto',
        columnGap: 8,
        alignItems: 'flex-start',
        fontSize: 12,
        fontWeight: 600,
        background: x.isActive ? (x.color ?? '#ffeb99') : '#d3d3d3',
        border: '2px solid #28a745',
        borderRadius: 4,
        cursor: 'pointer',
        paddingTop: 4,
        paddingBottom: 4,
      }}
    >
      {/* Left: incident type pill */}
      <div style={{ display: 'inline-flex', alignItems: 'center' }}>
        <mark
          className={`bg-${varnt} text-white px-1 rounded text-center`}
          style={{ whiteSpace: 'nowrap', margin: 0 }}
        >
          {x.incidentType.toUpperCase()}
        </mark>
      </div>

      {/* Middle: category text + location pill (AFTER the text, wraps if needed) */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 4,
          lineHeight: 1.2,
        }}
      >
        {/* category text – full, wrapping */}
        {catLabel && (
          <span
            style={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            {catLabel.toUpperCase()}
          </span>
        )}

        {/* location pill – AFTER type/category, in normal flow */}
        <span
          title={campusName || 'Primary Campus'}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '0.1rem 0.5rem',
            borderRadius: 9999,
            background: 'rgba(255,255,255,0.9)',
            border: '1px solid rgba(0,0,0,0.12)',
            fontSize: 12,
            fontWeight: 800,
            lineHeight: 1.2,
            color: '#212529',
            whiteSpace: 'normal',      // allow wrapping
            wordBreak: 'break-word',
          }}
        >
          {campusName || 'Primary Campus'}
        </span>

        {drill && (
          <span
            style={{
              padding: '0.05rem 0.35rem',
              borderRadius: 9999,
              background: 'rgba(0,128,0,0.12)',
              border: '1px solid rgba(0,128,0,0.3)',
              fontSize: 10,
              fontWeight: 900,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'rgba(0,100,0,0.9)',
            }}
          >
            Drill
          </span>
        )}
      </div>

      {/* Right: timestamp + 24h indicator (unchanged) */}
      <div className="text-end" style={{ lineHeight: 1.1 }}>
        <div className="small text-nowrap" title={x.createdAt || ''}>
          {ts}
        </div>
        <div
          className="small"
          style={{
            color: age.within24 ? '#198754' : '#6c757d',
            fontWeight: 600,
          }}
          title={age.rel ? `${age.rel} ago` : ''}
        >
          {age.within24 ? '≤24h' : '>24h'}
          {age.rel ? ` · ${age.rel}` : ''}
        </div>
      </div>
    </ListGroup.Item>
  );
});






  const handleUseSelection = async () => {
    if (!incidentType) { alert('Incident Type is required.'); return; }
    if (!incidentCategory) { alert('Pick a Category first.'); return; }

    try {
      const newIncident = await createIncidentAndDispatch();

      if (newIncident?.__existing) {
        await loadIncidents();
        setPendingIncident(null);
        setDeferOpen911(false);
        setShowTypeCatModal(false);

        const label  = canonicalTypeKey(incidentType);
        const campus = newIncident.__campusName || safeCampusName;

        alert(`An ACTIVE ${label} incident already exists for ${campus}. Opening it.`);
        navigate(`/activeincidentpage/${newIncident.incidentID}`, { state: { incident: newIncident } });
        return;
      }

      if (newIncident?.incidentID) {
        await loadIncidents();
        setPendingIncident(newIncident);
        setDeferOpen911(true);
      }
    } catch (e) {
      console.error('create/open from Next', e);
      alert('Failed to create the incident.');
    } finally {
      setStatusArr([]);   setGroupsArr(['Staff Roster']);
      setMessageText(''); setAttachment(null);
      setShowTypeCatModal(false);
    }
  };


const greenSwitchStyle = {
  backgroundColor: 'rgba(80, 255, 160, 0.6)',
  borderColor: 'rgba(80, 255, 160, 0.6)',
  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='white'/%3e%3c/svg%3e")`
};

const greenLabelStyle = {
  color: 'black' // 0.6 = translucent
};

  /* ─── RENDER ─────────────────────────────────────────────── */

  return (
    <>
      {/* Scoped CSS for verify context (911 launch) */}
      <style>{`
        .verify-alert-content { background-color:#ffe6e6 !important; border:3px solid #8b0000 !important; }
        .verify-alert-content .modal-header, .verify-alert-content .modal-footer { border-color:#8b0000 !important; }
        .verify-alert-content .modal-title { color:#8b0000 !important; font-weight:800 !important; }
        /* Fallback: some RB versions only support dialogClassName */
        .verify-alert-dialog .modal-content { background-color:#ffe6e6 !important; border:3px solid #8b0000 !important; }
        .verify-alert-dialog .modal-header, .verify-alert-dialog .modal-footer { border-color:#8b0000 !important; }
        .verify-alert-dialog .modal-title { color:#8b0000 !important; font-weight:800 !important; }
      `}</style>

      {/* Panel-scoped DRILL watermark appears ONLY after incident is created,
          and ONLY for that pending incident during the 911 step. */}
      {String(pendingIncident?.incidentMode || '').toUpperCase() === 'DRILL' && (
        <div
          aria-hidden="true"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1040,          // below Bootstrap modal (1050), above panel
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0.1,
            fontSize: '12vw',
            fontWeight: 900,
            color: 'green',
            textTransform: 'uppercase',
            userSelect: 'none',
            transform: 'rotate(-30deg)',
          }}
        >
          This&nbsp;is&nbsp;a&nbsp;Drill
        </div>
      )}

      <Card style={{ width:'100vw',height:'100vh',
                     border:'3px solid #f2c94c',borderRadius:0 }}>
 <DashboardCardHeader
  title={
    <div className="d-flex align-items-center gap-2">
      <span>Incident Alert</span>
      <Button
        variant="warning"
        size="sm"
        className="rounded-circle d-inline-flex align-items-center justify-content-center fw-bold"
        style={{
          width: 28,
          height: 28,
          lineHeight: 1,
          backgroundColor: '#2d39daff',
          color: '#fff',
          border: '2px solid #fff',
          boxShadow: '0 0 6px rgba(255,87,34,0.6)',
          transition: 'all 0.2s ease-in-out'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#ff5722';
          e.currentTarget.style.borderColor = '#d32f2f';
          e.currentTarget.style.boxShadow = '0 0 10px rgba(211,47,47,0.8)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#2d39daff';
          e.currentTarget.style.borderColor = '#fff';
          e.currentTarget.style.boxShadow = '0 0 6px rgba(255,87,34,0.6)';
        }}
        onClick={() => setShowHelp(true)}
        aria-label="How to use the Report Incident panel"
        title="How to use this panel"
      >
        ?
      </Button>
    </div>
  }
  onLogout={handleLogout}
  closePath="/schoolshield"
  hideDropdown
  logoOverride={logo}
/>

        <Card.Body className="d-flex flex-column pt-2 pb-3">
          <Container fluid>
            <div className={panelStyles.layoutShell}>

              <Row className="mt-0 gy-3">
                {hasMultipleAlertCampuses && (
                  <Alert variant="danger" className="mb-2" style={{ fontSize: '12px' }}>
 You have more than one assigned campus. Select the correct campus before sending the alert.
                  </Alert>
                )}

              </Row>

              <Row className="mt-0 gy-3">
                {/* Left: config card with selectors + 911 button */}
                <Col
                  xs={12}
                  md={7}
                  lg={6}
                  className={panelStyles.mainCol}
                >
 

                  <div className={panelStyles.configCard}>
                    {/* Campus selector row */}
                    <div className={panelStyles.selectorRow}>
                      <div className={panelStyles.selectorControl}>
                        <div className={panelStyles.fieldHeaderRow}>
                          <span className={panelStyles.fieldLabel}>
                            Select Campus / Building
                          </span>
                        </div>
 <DropdownSearchInfrastructureAlert
  databaseContent={assignedCampusBuildingInfos}
  selectedID={selID}
  onSelect={handleSelectCampus}
  onMissingPrimaryCampus={handleMissingPrimaryCampusDetected}
/>
                      </div>
                    </div>

                    {/* Incident type row */}
                    <div className={panelStyles.selectorRow}>
                      <div className={panelStyles.selectorControl}>
                        <div className={panelStyles.fieldHeaderRow}>
                          <span className={panelStyles.fieldLabel}>
                            Select Incident Type
                          </span>
                        </div>
                        <Button
                          variant="outline-primary"
                          className={panelStyles.selectorButton}
                          onClick={() => setShowType(true)}
                        >
                          {incidentType
                            ? `${incidentType}${
                                incidentCategory
                                  ? ' – ' + incidentCategory
                                  : ''
                              }`
                            : 'Select Incident Type'}
                        </Button>
                      </div>
                    </div>

                    <div
  style={{
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: 8,
    marginTop: 10,
  }}
>
 

 
</div>

                    {/* 911 button */}
                    <div className={panelStyles.emergencyButtonWrapper}>
                      <Button
                        variant="danger"
                        className={panelStyles.emergencyButton}
                        onClick={openVerifyTypeCategoryModal}
                      >
                        <ExclamationTriangleFill
                          size={32}
                          className="me-2"
                        />
                        <span className={panelStyles.emergencyText}>
                          Report {incidentType} Incident and Dial 911
                        </span>
                      </Button>
                    </div>
                  </div>
                </Col>

                {/* Right: admin tools card */}
                <Col
                  xs={12}
                  md={5}
                  lg={4}
                  className={panelStyles.adminCol}
                >
                  {isAdmin && (
                    <div className={panelStyles.adminCard}>
                      <div className={panelStyles.adminHeader}>
                        Administrator Tools
                      </div>
                      <div className={panelStyles.adminBody}>
                        <div className={panelStyles.adminButtonRow}>
                          <Button
                            size="sm"
                            variant={
                              effectiveShowAll
                                ? 'outline-primary'
                                : 'secondary'
                            }
                            onClick={() => setShowAll((p) => !p)}
                          >
                            {effectiveShowAll
                              ? 'Show Last 24h'
                              : 'Show All Incidents'}
                          </Button>

                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() =>
                              navigate('/dashboard', {
                                state: {
                                  iCampusID: '-99',
                                  iBuildingID: '-99',
                                  activeOnly: effectiveShowAll ? '0' : '1',
                                },
                              })
                            }
                          >
                            Incident Monitor
                          </Button>
                        </div>

                                       <div
                          className={panelStyles.adminToggleRow}
                          style={{
                            display: 'flex',
                            gap: '1.25rem',
                            flexWrap: 'wrap',
                            alignItems: 'center'
                          }}
                        >



                          {/* <Form.Check
                            type="switch"
                            id="toggle-drill-mode"
                            checked={modeIsDrill}
                            onChange={(e) => {
                              const v = e.target.checked;
                              setModeIsDrill(v);
                              sessionStorage.setItem(
                                'panelModeIsDrill',
                                v ? '1' : '0'
                              );
                              if (v) {
                                localStorage.setItem('isTestIncidentOverride', '1');
                              } else {
                                localStorage.removeItem('isTestIncidentOverride');
                              }
                            }}
                            label={
                              modeIsDrill
                                ? 'DRILL (test mode)'
                                : 'REAL (live)'
                            }
                            style={{
                              fontSize: '0.85rem',
                              display: 'flex',
                              alignItems: 'center',
                              fontWeight: '700'
                            }}
                            className="align-items-center"
                          />

                          <Form.Check
                            type="switch"
                            id="toggle-silent-mode"
                            checked={modeIsSilent}
                            onChange={(e) => setModeIsSilent(e.target.checked)}
                            label={
                              modeIsSilent
                                ? 'SILENT (no SMS/voice/email)'
                                : 'LOUD (send SMS/voice/email)'
                            }
                            style={{
                              fontSize: '0.85rem',
                              display: 'flex',
                              alignItems: 'center',
                              fontWeight: '700'
                            }}
                            className="align-items-center"
                          /> */}

<div
  className="form-check form-switch align-items-center mb-0"
  style={{
    fontSize: '0.85rem',
    display: 'flex',
    alignItems: 'center',
    fontWeight: '700',
    gap:'5px'
  }}
>
  <input
    className="form-check-input"
    type="checkbox"
    role="switch"
    id="toggle-drill-mode"
    checked={modeIsDrill}
    onChange={(e) => {
      const v = e.target.checked;
      setModeIsDrill(v);
      sessionStorage.setItem('panelModeIsDrill', v ? '1' : '0');

      if (v) {
        localStorage.setItem('isTestIncidentOverride', '1');
      } else {
        localStorage.removeItem('isTestIncidentOverride');
      }
    }}
    style={!modeIsDrill ? greenSwitchStyle : undefined}
  />
  <label
    className="form-check-label"
    htmlFor="toggle-drill-mode"
    style={!modeIsDrill ? greenLabelStyle : undefined}
  >
    {modeIsDrill ? 'DRILL (test mode)' : 'REAL (live)'}
  </label>
</div>

<div
  className="form-check form-switch align-items-center mb-0"
  style={{
    fontSize: '0.85rem',
    display: 'flex',
    alignItems: 'center',
    fontWeight: '700',
    gap:'5px'
  }}
>
  <input
    className="form-check-input"
    type="checkbox"
    role="switch"
    id="toggle-silent-mode"
    checked={modeIsSilent}
    onChange={(e) => setModeIsSilent(e.target.checked)}
    style={!modeIsSilent ? greenSwitchStyle : undefined}
  />
  
  <label
    className="form-check-label"
    htmlFor="toggle-silent-mode"
    style={!modeIsSilent ? greenLabelStyle : undefined}
  >
   
    {modeIsSilent
      ? 'SILENT (no SMS/voice/email)'
      : 'LOUD (send SMS/voice/email)'}
  </label>
</div>






                        </div>


                      </div>
                    </div>
                  )}
                </Col>
              </Row>

              {visibleIncidents.length > 0 && (
                <>
                  <hr className="p-1" />
                  <Row className="align-items-center mb-1">
                    <Col>
                      <h6 className="text-center m-0">
                        {effectiveShowAll
                          ? 'All Incidents (select row to open)'
                          : 'Incidents – Last 24 Hours (select row to open)'}
                      </h6>
                    </Col>
                  </Row>

                  <Row style={{ overflow: 'hidden' }}>
                    <div
                      style={{
                        maxHeight: '35vh',
                        overflowY: 'auto',
                        width: '100%',
                        paddingRight: 2,
                      }}
                    >
                      <ListGroup>{rows}</ListGroup>
                    </div>
                  </Row>
                </>
              )}
            </div>
          </Container>
        </Card.Body>

      </Card>

      {/* modal components – logic unchanged */}
      <IncidentTypeModal show={showType} onHide={()=>setShowType(false)}
        onSave={({incidentType,category})=>{
          setIncidentType(incidentType); setIncidentCategory(category);
        }}/>

 <ReportStatusModal
  show={showStatus}
  onHide={() => setShowStatus(false)}
  onSave={setStatusArr}
  initial={statusArr}
  specialStudentScope={specialStudentScope}
  user={user}
  actorName={fullName}
/>
 



      <GroupsModal show={showGroups} onHide={()=>setShowGroups(false)}
        onSave={setGroupsArr} initial={groupsArr}/>
      <MessageMediaModal show={showMsg} onHide={()=>setShowMsg(false)}
        onSave={({text,attachment})=>{
          setMessageText(text); setAttachment(attachment);
        }}/>
      <SendStatusModal show={showSendModal} onHide={()=>setShowSendModal(false)}
        onSend={sendAlert} summary={{
          fullName, location:safeCName, statusArr,
          groupsArr, messageText, attachment
        }}
        openLocation={()=>{setShowSendModal(false);setShowGroups(false);}}
        openStatus  ={()=>{setShowSendModal(false);setShowStatus(true );}}
        openGroups  ={()=>{setShowSendModal(false);setShowGroups(true );}}
        openMessage ={()=>{setShowSendModal(false);setShowMsg(true   );}}
      />

      {/* Help modal */}
      <Modal show={showHelp} onHide={() => setShowHelp(false)} centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>How to use the Report Incident panel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ol className="mb-0 ps-3">
            <li>Set <b>Campus</b> and <b>Incident Type/Category</b> (defaults usually OK).</li>
                        <li>Admins can toggle <b>DRILL/REAL</b> and <b>SILENT/LOUD</b>. Silent suppresses SMS, voice, and email only; incident creation, DB activity, and webpush still continue.</li>
            <li><b>Next</b> creates & opens the incident; messages fan out.</li>
            <li>If a duplicate is blocked, it’s because an <b>ACTIVE same‑type</b> incident already exists in the <b>last 24h</b>. Closed incidents in the last 24h do <i>not</i> block.</li>
            <li>Select any row below to open its incident.</li>
          </ol>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowHelp(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Type/Category modal (Type locked). Auto-creates on "User Selection"
          NOTE: Styled + retitled ONLY when launched via 911 button */}
      <Modal
        show={showTypeCatModal}
        onHide={() => { setShowTypeCatModal(false); setIsVerifyContext(false); }}
        centered
        onExited={() => {
          if (deferOpen911 && pendingIncident) {
            setDeferOpen911(false);
            open911Modal(pendingIncident);
          }
          setIsVerifyContext(false); // reset after close
        }}
        dialogClassName={isVerifyContext ? 'verify-alert-dialog' : undefined}
        contentClassName={isVerifyContext ? 'verify-alert-content' : undefined}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isVerifyContext
              ? 'VERIFY INCIDENT TYPE AND CATEGORY BEFORE SENDING ALERT'
              : 'Select Category'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <AlertTypeCategorySelector
            key={typeCatModalKey}
            selectedType={incidentType}
            selectedCategory={incidentCategory}
            onTypeChange={setIncidentType}
            onCategoryChange={handleCategoryForModal}
            disableType={true}
          />
          <div className="mt-2 small text-muted">
            Incident Type is locked to <b>{incidentType || 'Lockdown'}</b>. Pick a Category.
            Choosing <b>User Selection</b> will immediately create and open the incident.
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button className="me-auto" variant="secondary" onClick={() => { setShowTypeCatModal(false); setIsVerifyContext(false); }}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUseSelection}
            disabled={!incidentCategory || isPosting}
          >
            {isPosting ? <Spinner animation="border" size="sm" className="me-2" /> : null}
            Next
          </Button>
        </Modal.Footer>
      </Modal>


      <NoPrimaryCampusModal
        show={showMissingPrimaryCampus}
        onClose={handleMissingPrimaryCampusClose}
      />

      {/* 911: swipe-to-call modal */}
      <Modal
        show={showCall911}
        onHide={() => {}}
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Header>
          <Modal.Title>Dial 911?</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="mb-2">
            Do you wish to dial <b>911</b> now?
          </div>
          <div className="text-muted small mb-3">
            Swipe right to call 911, or select Send Now to continue without calling.
          </div>

          <div className="px-1">
            <Form.Range
              value={dialProgress}
              min={0}
              max={100}
              step={1}
              onChange={(e) => setDialProgress(Number(e.target.value))}
              onMouseUp={handleSliderRelease}
              onTouchEnd={handleSliderRelease}
            />
            <div className="text-center fw-semibold">
              {dialProgress >= 95 ? 'Release to call' : 'Swipe right to call 911'}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={navigateToPendingIncident}>
            Send Alert Now!
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

 


// /* ────────────────────────────────────────────────────────────────
//  *  ReportIncidentPanel.jsx – full file (updated clean UI)
//  * ──────────────────────────────────────────────────────────────── */
//  import React, {
//   useState, useEffect, useContext, useCallback, useRef, useMemo,
// } from 'react';

// import {
//   Card, Container, Row, Col, ListGroup, Button, Modal, Spinner, Form, Alert,
// } from 'react-bootstrap';
// import AlertTypeCategorySelector from './AlertTypeCategorySelector';

// import { useNavigate } from 'react-router-dom';
// import { ExclamationTriangleFill } from 'react-bootstrap-icons';

// import DashboardCardHeader               from '../ViewReports/DashboardCardHeader';
// import DropdownSearchInfrastructureAlert from './DropdownSearchInfrastructureAlert';
// import IncidentTypeModal                 from './IncidentTypeModal';
// import ReportStatusModal                 from './ReportStatusModal';
// import GroupsModal                       from './GroupsModal';
// import MessageMediaModal                 from './MessageMediaModal';
// import SendStatusModal                   from './SendStatusModal';
// import NoPrimaryCampusModal              from './NoPrimaryCampusModal';
// import logo                              from '../ViewReports/SchoolShield_color300x3xx.webp';

// import { UserContext } from '../../userAlertContext';
// import axios           from '../axiosAlertConfig';
// import panelStyles     from './ReportIncidentPanel.module.css';

// /* ───────── helpers ───────── */
// const DBG = false;
// const dbg = (m,o)=>DBG&&console.log('[ReportIncidentPanel]',m,o??'');
// const up   = v => (v ?? '').toString().toUpperCase();

// const uniqStrings = (values = []) =>
//   [...new Set((values || []).map((v) => String(v)).filter(Boolean))];

// // Canonicalize human labels to a stable key
// const canonicalTypeKey = (t) => {
//  const k = (t ?? '').toLowerCase();
//  if (k.includes('lock'))    return 'lockdown';
//  if (k.includes('evac'))    return 'evacuate';
//  if (k.includes('secure'))  return 'secure';
//  if (k.includes('shelter')) return 'shelter';
//  if (k.includes('hold'))    return 'hold';
//   return k.trim();
// };

// const colour = (t) => {
//   const k = (t ?? '').toLowerCase();
//   if (k.includes('lock'))    return 'danger';
//   if (k.includes('evac'))    return 'warning';
//   if (k.includes('secure'))  return 'primary';
//   if (k.includes('shelter')) return 'info';
//   if (k.includes('hold'))    return 'secondary';
//   return 'dark';
// };

// const API = '/ialert/incidents';


// /* scoped caches so Show All doesn’t pollute default view
//    and non-admins are campus-scoped */
// const CACHE_ALL    = 'incidentsCache:all';
// const CACHE_RECENT = 'incidentsCache:active24h';
// const cacheKeyFor  = (useAll, campusId, isPrivileged) =>
//   useAll
//     ? CACHE_ALL
//     : (isPrivileged ? CACHE_RECENT : `${CACHE_RECENT}:campus:${campusId || 'none'}`);

// /* ─── MAIN COMPONENT ──────────────────────────────────────────── */
// export default function ReportIncidentPanel() {
//   const navigate = useNavigate();
//   const {
//     user, iBuildingID, buildingName, isTestIncident, /* decoupled: setIsTestIncident */
//   } = useContext(UserContext);

//   const autoOpenDoneRef = useRef(false);


//   // Default category to use when the Verify (Type/Category) modal opens with no category chosen yet
// const DEFAULT_CATEGORY_BY_TYPE = {
//   lockdown: 'Active Shooter',
// };

// const defaultCategoryForType = (type) =>
//   DEFAULT_CATEGORY_BY_TYPE[canonicalTypeKey(type)] || '';


//   // ─── Smooth navigation guard (TTL-based) ──────────────────────────
//   const AUTO_SUPPRESS_MS = 3000;
//   const shouldSuppressAutoOpen = () => {
//     const now = Date.now();
//     const until = Number(sessionStorage.getItem('suppressAutoOpenUntil') || 0);
//     if (until > now) return true;
//     const activeOpenAt = Number(sessionStorage.getItem('activeOpenAt') || 0);
//     if (activeOpenAt && (now - activeOpenAt) < AUTO_SUPPRESS_MS) return true;
//     return false;
//   };

//   useEffect(() => {
//     if (sessionStorage.getItem('skipAutoOpenOnce') === '1') {
//       sessionStorage.removeItem('skipAutoOpenOnce');
//       sessionStorage.setItem('suppressAutoOpenUntil', String(Date.now() + AUTO_SUPPRESS_MS));
//     }
//     if (shouldSuppressAutoOpen()) autoOpenDoneRef.current = true;
//   }, []);

//   // ── Local drill mode only (no global sync) ────────────────────────
//   const [modeIsDrill, setModeIsDrill] = useState(() => {
//     if (localStorage.getItem('isTestIncidentOverride') === '1') return true;
//     const v = sessionStorage.getItem('panelModeIsDrill');
//     return v !== null ? v === '1' : !!isTestIncident;
//   });

//   const [modeIsSilent, setModeIsSilent] = useState(() => {
//     const v = sessionStorage.getItem('panelModeIsSilent');
//     return v !== null ? v === '1' : false;   // default = LOUD
//   });


//   //////////////////////////////////////////////////////////////////
//   // 911 modal state & helpers
//   const [showCall911, setShowCall911] = useState(false);
//   const [pendingIncident, setPendingIncident] = useState(null);
//   const [dialProgress, setDialProgress] = useState(0);
//   const [deferOpen911, setDeferOpen911] = useState(false);

//   const open911Modal = (incident) => {
//     setPendingIncident(incident);
//     setDialProgress(0);
//     setShowCall911(true);
//   };

//   const navigateToPendingIncident = () => {
//     const inc = pendingIncident;
//     setShowCall911(false);
//     setPendingIncident(null);
//     if (inc?.incidentID) {
//       navigate(`/activeincidentpage/${inc.incidentID}`, { state: { incident: inc } });
//     }
//   };

//   const handleDial911 = () => {
//     try {
//       window.location.href = 'tel:911';
//     } finally {
//       navigateToPendingIncident();
//     }
//   };

//   const handleSliderRelease = () => {
//     if (dialProgress >= 95) {
//       handleDial911();
//     } else {
//       setDialProgress(0);
//     }
//   };

//   // ─── time helpers ───────────────────────────────
//   const MS_HOUR = 3600000;
//   const MS_DAY  = 24 * MS_HOUR;
//   const fmtTS = (iso) => {
//     if (!iso) return '';
//     const d = new Date(iso);
//     return d.toLocaleString(undefined, {
//       month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit'
//     });
//   };
//   const ageFromNow = (iso) => {
//     if (!iso) return { within24:false, rel:'' };
//     const ms = Date.now() - new Date(iso).getTime();
//     const within24 = ms <= MS_DAY;
//     const h = Math.floor(ms / MS_HOUR);
//     const m = Math.floor((ms % MS_HOUR) / 60000);
//     return { within24, rel: `${h}h ${m}m` };
//   };

//   // Persist local drill mode only
//   useEffect(() => {
//     sessionStorage.setItem('panelModeIsDrill', modeIsDrill ? '1' : '0');
//   }, [modeIsDrill]);

//   useEffect(() => {
//     sessionStorage.setItem('panelModeIsSilent', modeIsSilent ? '1' : '0');
//   }, [modeIsSilent]);


//   const handleMissingPrimaryCampusClose = useCallback(() => {
//     sessionStorage.removeItem('defaultCampusID');
//     sessionStorage.removeItem('defaultCampusName');
//     sessionStorage.removeItem('skipAutoOpenOnce');
//     sessionStorage.removeItem('suppressAutoOpenUntil');
//     navigate('/login', { replace: true });
//   }, [navigate]);

//  const handleLogout = useCallback(() => {
//   sessionStorage.removeItem('defaultCampusID');
//   sessionStorage.removeItem('defaultCampusName');
//   sessionStorage.removeItem('skipAutoOpenOnce');
//   sessionStorage.removeItem('suppressAutoOpenUntil');
//   sessionStorage.removeItem('panelModeIsDrill');
//   sessionStorage.removeItem('panelModeIsSilent');
//   sessionStorage.removeItem('incidentsShowAll');
//   sessionStorage.removeItem('incidentsCache');
//   sessionStorage.removeItem(CACHE_ALL);
//   sessionStorage.removeItem(CACHE_RECENT);
//   sessionStorage.removeItem('activeOpenAt');

//   for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
//     const key = sessionStorage.key(i);
//     if (key && key.startsWith(`${CACHE_RECENT}:campus:`)) {
//       sessionStorage.removeItem(key);
//     }
//   }

//   localStorage.removeItem('isTestIncidentOverride');

//   navigate('/login', { replace: true });
// }, [navigate]);


//   /* ── load BuildingInfos once ── */
//    /* ── load BuildingInfos once ── */
//   const [buildingInfos, setBuildingInfos] = useState([]);
//   const [campusGroups, setCampusGroups] = useState({});

//   useEffect(() => {
//     if (!user?.iAccountID) return;

//     axios.get('/buildinginfos', { params: { iAccountID: user.iAccountID } })
//       .then(({ data }) => setBuildingInfos(data || []))
//       .catch((e) => {
//         console.error('load buildings', e);
//         setBuildingInfos([]);
//       });
//   }, [user?.iAccountID]);

//   useEffect(() => {
//     if (!user?.iAccountID) {
//       setCampusGroups({});
//       return;
//     }

//     axios.get('/CampusBuildings', { params: { iAccountID: user.iAccountID } })
//       .then(({ data }) => {
//         const rawGroups = data?.groups || {};
//         const normalized = {};

//         Object.keys(rawGroups).forEach((campusId) => {
//           normalized[String(campusId)] = uniqStrings(
//             Array.isArray(rawGroups[campusId]) ? rawGroups[campusId] : []
//           );
//         });

//         setCampusGroups(normalized);
//       })
//       .catch((e) => {
//         console.error('load campus groups', e);
//         setCampusGroups({});
//       });
//   }, [user?.iAccountID]);

//   const mainCampusIdByBuildingId = useMemo(() => {
//     const out = {};

//     Object.keys(campusGroups || {}).forEach((campusId) => {
//       const sid = String(campusId);
//       out[sid] = sid;

//       uniqStrings(campusGroups[campusId] || []).forEach((buildingId) => {
//         out[String(buildingId)] = sid;
//       });
//     });

//     return out;
//   }, [campusGroups]);

//   const mainCampusIdSet = useMemo(
//     () => new Set(Object.keys(campusGroups || {}).map(String)),
//     [campusGroups]
//   );

//   const normalizeIDsToMainCampuses = useCallback((ids = []) => {
//     const raw = uniqStrings(ids);

//     if (!raw.length) return [];
//     if (!mainCampusIdSet.size) return raw;

//     return uniqStrings(
//       raw
//         .map((id) => mainCampusIdByBuildingId[String(id)] || String(id))
//         .filter((id) => mainCampusIdSet.has(String(id)))
//     );
//   }, [mainCampusIdByBuildingId, mainCampusIdSet]);

//   const mainCampusBuildingInfos = useMemo(() => {
//     if (!Array.isArray(buildingInfos) || !buildingInfos.length) return [];
//     if (!mainCampusIdSet.size) return [];

//     const arr = buildingInfos.filter((b) =>
//       mainCampusIdSet.has(String(b.iBuildingID))
//     );

//     arr.sort((a, b) =>
//       String(a?.BuildingName || '').localeCompare(
//         String(b?.BuildingName || ''),
//         undefined,
//         { sensitivity: 'base' }
//       )
//     );

//     return arr;
//   }, [buildingInfos, mainCampusIdSet]);


// const normalizedUserCampusIDs = useMemo(
//   () => normalizeIDsToMainCampuses(user?.campusIDs || []),
//   [user?.campusIDs, normalizeIDsToMainCampuses]
// );

// const assignedCampusBuildingInfos = useMemo(() => {
//   const assignedSet = new Set((normalizedUserCampusIDs || []).map(String));

//   const arr = (buildingInfos || []).filter((b) =>
//     assignedSet.has(String(b.iBuildingID))
//   );

//   arr.sort((a, b) =>
//     String(a?.BuildingName || '').localeCompare(
//       String(b?.BuildingName || ''),
//       undefined,
//       { sensitivity: 'base' }
//     )
//   );

//   return arr;
// }, [buildingInfos, normalizedUserCampusIDs]);




//   const primaryCampusId = normalizedUserCampusIDs[0] ?? '';

//   const resolvedPrimaryCampusName = useMemo(() => {
//     return (
//       buildingInfos.find((b) => String(b.iBuildingID) === String(primaryCampusId))?.BuildingName ||
//       user?.campusNames?.[0] ||
//       ''
//     );
//   }, [buildingInfos, primaryCampusId, user?.campusNames]);

//   /**
//    * Auto-open only if ACTIVE incident in last 24h for user's primary campus.
//    */
//   const autoOpenLatestIfAny = useCallback(async () => {
//     if (autoOpenDoneRef.current) return;
//     if (shouldSuppressAutoOpen()) { autoOpenDoneRef.current = true; return; }



//     const acctID   = user?.iAccountID;
//     const campusID = primaryCampusId;
//     if (!acctID || !campusID) return;

//     try {
//       const { data } = await axios.get(API, {
//         params: {
//           iAccountID : acctID,
//           iCampusID  : campusID,
//           iBuildingID: '-99',
//           activeOnly : '1',
//           hours      : 24,
//         },
//       });

//       const list = Array.isArray(data) ? data.slice() : [];
//       if (!list.length) { autoOpenDoneRef.current = true; return; }

//       const now = Date.now();
//       const within24Active = list.filter(r =>
//         Number(r.isActive) === 1 &&
//         (now - new Date(r.createdAt).getTime()) <= MS_DAY
//       );

//       if (!within24Active.length) { autoOpenDoneRef.current = true; return; }

//       within24Active.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
//       const target = within24Active[0];

//       sessionStorage.setItem('skipAutoOpenOnce', '1');
//       autoOpenDoneRef.current = true;

//       navigate(`/activeincidentpage/${target.incidentID}`, {
//         state: { incident: target },
//         replace: true
//       });
//     } catch (e) {
//       console.error('[auto-open] failed', e);
//     }
//    }, [user?.iAccountID, primaryCampusId, navigate]);

//   useEffect(() => { autoOpenLatestIfAny(); }, [autoOpenLatestIfAny]);

//   /* ── campus selection state (default = Users JSON [0]) ── */
//    /* ── campus selection state (default = normalized main campus) ── */
//   const initID = primaryCampusId || '';
//   const initName = resolvedPrimaryCampusName || user?.campusNames?.[0] || '';

//   // show warning when user has more than one assigned main campus
//   const hasMultipleAlertCampuses = normalizedUserCampusIDs.length > 1;

//   const roleLower    = (user?.userRole || '').toLowerCase();
//   const isAdmin      = ['administrator','systemadministrator'].includes(roleLower);
//   const isPrivileged = isAdmin;

//   const [selID, setSelID] = useState(
//     () => sessionStorage.getItem('defaultCampusID') || initID
//   );
//   const [selName, setSelName] = useState(
//     () => sessionStorage.getItem('defaultCampusName') || initName
//   );

//   const normalizeCampusSelection = useCallback((rawId, rawName = '') => {
//     const ids = String(rawId || '')
//       .split(',')
//       .map((s) => s.trim())
//       .filter(Boolean);

//     if (!ids.length) {
//       return { id: '', name: '', ids: [] };
//     }

//     if (!mainCampusIdSet.size) {
//       return {
//         id: ids.join(','),
//         name: String(rawName || ''),
//         ids,
//       };
//     }

//     const normalizedIds = normalizeIDsToMainCampuses(ids);
//     const normalizedNames = normalizedIds
//       .map((id) =>
//         buildingInfos.find((b) => String(b.iBuildingID) === String(id))?.BuildingName || ''
//       )
//       .filter(Boolean);

//     return {
//       id: normalizedIds.join(','),
//       name: normalizedNames.join(', '),
//       ids: normalizedIds,
//     };
//   }, [mainCampusIdSet, normalizeIDsToMainCampuses, buildingInfos]);


//  useEffect(() => {
//   const assignedIds = new Set((normalizedUserCampusIDs || []).map(String));

//   const currentIds = String(selID || '')
//     .split(',')
//     .map((s) => s.trim())
//     .filter(Boolean);

//   const currentIsAssigned =
//     currentIds.length > 0 &&
//     currentIds.every((id) => assignedIds.has(String(id)));

//   const id = primaryCampusId;
//   const name = resolvedPrimaryCampusName;

//   if ((!selID || !currentIsAssigned) && id) {
//     setSelID(id);
//     setSelName(name || 'Campus');
//     sessionStorage.setItem('defaultCampusID', id);
//     sessionStorage.setItem('defaultCampusName', name || 'Campus');
//   }
// }, [
//   selID,
//   primaryCampusId,
//   resolvedPrimaryCampusName,
//   normalizedUserCampusIDs,
// ]);

//   useEffect(() => {
//     if (!selID || !mainCampusIdSet.size) return;

//     const normalized = normalizeCampusSelection(selID, selName);
//     const nextId = normalized.id || selID;
//     const nextName = normalized.name || selName;

//     if (nextId !== selID || nextName !== selName) {
//       setSelID(nextId);
//       setSelName(nextName);
//       sessionStorage.setItem('defaultCampusID', nextId);
//       sessionStorage.setItem('defaultCampusName', nextName);
//     }
//   }, [selID, selName, mainCampusIdSet, normalizeCampusSelection]);

//   const handleSelectCampus = ({ id, name }) => {
//     const normalized = normalizeCampusSelection(id, name);
//     const nextId = normalized.id || String(id || '');
//     const nextName = normalized.name || String(name || '');

//     dbg('dropdown → onSelect', { id: nextId, name: nextName });

//     setSelID(nextId);
//     setSelName(nextName);
//     sessionStorage.setItem('defaultCampusID', nextId);
//     sessionStorage.setItem('defaultCampusName', nextName);
//   };






//   /* ── incidents cache / refresh logic ── */
//   const fullName   = [user?.firstName,user?.lastName].filter(Boolean).join(' ');
//   const acctID     = user?.iAccountID ?? '';
//   const uid        = user?.userID     ?? 0;
//   const safeCID    = selID   || '-99';
//   const safeCName  = selName || '';
//   const safeBID    = iBuildingID?.trim() ? iBuildingID : '-99';

//   const safeBldgID     = iBuildingID?.trim() ? iBuildingID : '-99';
//   const safeCampusID   = safeCID || '-99';
//   const safeCampusName = selName || '';
//   const safeAcctID     = user?.iAccountID  ?? '';
//   const safeUID        = user?.userID      ?? 0;
//   const safeFName      = user?.firstName   ?? '';
//   const safeLName      = user?.lastName    ?? '';

//   const specialStudentScope = useMemo(() => {
//   const selectedIds = String(selID || '')
//     .split(',')
//     .map((item) => item.trim())
//     .filter(Boolean);

//   const resolvedBuildingId =
//     selectedIds[0] ||
//     primaryCampusId ||
//     safeBID ||
//     safeBldgID ||
//     '-99';

//   return {
//     iAccountID: String(safeAcctID || '').trim(),
//     iCampusID: '-99',
//     iBuildingID: String(resolvedBuildingId || '').trim(),
//   };
// }, [selID, primaryCampusId, safeAcctID, safeBID, safeBldgID]);


//   //const primaryCampusId = user?.campusIDs?.[0] ?? '';

//   const [posting,setPosting] = useState(false);

//   // default: Show All OFF (non-admins are always 24h)
//   const [showAll, setShowAll] = useState(() =>
//     (isAdmin && sessionStorage.getItem('incidentsShowAll') === '1')
//   );

//   const effectiveShowAll = isAdmin ? showAll : false;

//   /** Build authorized target building IDs from current selection. */
//    /** Build authorized target main campus IDs from current selection. */
//   const getAuthorizedTargetIDs = React.useCallback(() => {
//     const rawSel = selID || '';



//  const userPrimaryIDs = normalizeIDsToMainCampuses(user?.campusIDs || []);


//     let selected = [];
//  if (rawSel === '-99') {
//   selected = userPrimaryIDs;
//     } else if (rawSel.includes(',')) {
//       selected = normalizeIDsToMainCampuses(
//         rawSel.split(',').map((s) => s.trim()).filter(Boolean)
//       );
//     } else if (rawSel) {
//       selected = normalizeIDsToMainCampuses([rawSel]);
//     }

// const authorized = selected.filter((id) =>
//   userPrimaryIDs.includes(String(id))
// );

//     return uniqStrings(authorized);
//  }, [selID, user?.campusIDs, normalizeIDsToMainCampuses]);

//   /* 🆕 Uniform relaxed policy for all campuses:
//      Block ONLY if there is an ACTIVE same‑type incident in <24h for this campus.
//      Different types never block. Closed same‑type <24h does not block.
//   */
//   const findRecentIncidentByType = useCallback(async (type, campusId) => {
//     if (!safeAcctID || !campusId) return null;
//     try {
//       const { data } = await axios.get(API, {
//         params: {
//           iAccountID : safeAcctID,
//           iCampusID  : campusId,
//           iBuildingID: '-99',
//           activeOnly : '0',
//           hours      : 24,
//         },
//       });
//       const list = Array.isArray(data) ? data : [];
//       const keyWanted = canonicalTypeKey(type);
//       const matches = list.filter(r => canonicalTypeKey(r.incidentType) === keyWanted);
//       if (!matches.length) return null;

//       // Uniform relaxed rule for everyone:
//       const activeSameType = matches.find(r => Number(r.isActive) === 1);
//       return activeSameType || null;
//     } catch (e) {
//       console.error('dup-check failed', e);
//       return null;
//     }
//   }, [safeAcctID]);

//   // if role changes to non-admin, force 24h + persist
//   useEffect(() => {
//     if (!isAdmin && showAll) {
//       setShowAll(false);
//       sessionStorage.setItem('incidentsShowAll', '0');
//     }
//   }, [isAdmin, showAll]);

//   // hydrate incidents from the correct scoped cache (campus-scoped for non-admins)
//    const [incidents,setIncidents] = useState(() => {
//     try {
//       const useAllAtInit = (sessionStorage.getItem('incidentsShowAll') === '1');
//       const key = cacheKeyFor(isAdmin && useAllAtInit, (primaryCampusId || ''), isAdmin);
//       return JSON.parse(sessionStorage.getItem(key) || '[]');
//     } catch { return []; }
//   });

//   // LOAD: role-gated list scope
//   const loadIncidents = useCallback(async () => {
//     if (!acctID) return;

//     const useAll = effectiveShowAll;
//     const campusForList = isAdmin ? '-99' : (primaryCampusId || '');
//     if (!isAdmin && !campusForList) return;

//     try {
//       const params = {
//         iAccountID:  acctID,
//         iCampusID:   campusForList,
//         iBuildingID: '-99',
//         activeOnly:  useAll ? '0' : '1',
//         ...(useAll ? {} : { hours: 24 }),
//       };
//       const { data } = await axios.get(API, { params });
//       setIncidents(data);
//       sessionStorage.setItem(cacheKeyFor(useAll, campusForList, isAdmin), JSON.stringify(data));
//       sessionStorage.setItem('incidentsCache', JSON.stringify(data)); // legacy
//     } catch (e) {
//       console.error('fetch incidents', e);
//     }
//   }, [acctID, effectiveShowAll, isAdmin, primaryCampusId]);

//   useEffect(() => {
//     sessionStorage.setItem('incidentsShowAll', effectiveShowAll ? '1' : '0');
//     try {
//       const campusForList = isAdmin ? '-99' : (primaryCampusId || '');
//       const cached = JSON.parse(sessionStorage.getItem(cacheKeyFor(effectiveShowAll, campusForList, isAdmin)) || '[]');
//       if (Array.isArray(cached)) setIncidents(cached);
//     } catch {}
//   }, [effectiveShowAll, isAdmin, primaryCampusId]);

//   useEffect(() => { if (!posting) loadIncidents(); }, [posting, effectiveShowAll, loadIncidents]);

//   /* ------------------------------------------------------------------ *
//    *  sendAlert() – create the incident, then FAN-OUT via /dispatch
//    * ------------------------------------------------------------------ */
//   const [isPosting, setIsPosting] = useState(false);

//   const sendAlert = async () => {
//     if (!incidentType) { alert('Select an incident type first'); return; }

//     const targetIDs = getAuthorizedTargetIDs();
//     if (!targetIDs.length) { alert('No authorized buildings selected.'); return; }

//     const primaryCampusIdLocal = targetIDs[0];
//     const primaryCampusName =
//       (buildingInfos.find(b => String(b.iBuildingID) === String(primaryCampusIdLocal))?.BuildingName) ||
//       (user?.campusNames?.[0] ?? safeCampusName);

//     // 🆕 Uniform campus‑scoped duplicate gate
//     const dup = await findRecentIncidentByType(incidentType, primaryCampusIdLocal);
//     if (dup) {
//       const label = canonicalTypeKey(incidentType);
//       alert(`An ACTIVE ${label} incident already exists for ${primaryCampusName}. Opening it instead.`);
//       navigate(`/activeincidentpage/${dup.incidentID}`, { state: { incident: dup } });
//       return;
//     }

//     const modeNow = modeIsDrill ? 'DRILL' : 'REAL';

//     try {
//       setIsPosting(true);
//       const { data } = await axios.post(API, {
//         iAccountID  : safeAcctID,
//         iCampusID   : primaryCampusIdLocal,
//         iBuildingID : primaryCampusIdLocal,
//         buildingName: primaryCampusName,
//         incidentType,
//         incidentCategory,
//         incidentMode : modeNow,
//         createdByUserID      : safeUID,
//         createdByName        : fullName,
//         createdByMobileNumber: user?.UserPhoneNumber || '',
//         iUserID     : safeUID,
//         userName    : user?.userName || user?.username || '',
//         myStatus      : statusArr,
//         sendToGroups  : groupsArr,
//         messageText   : messageText,
//         attachmentType: attachment?.type ?? null,
//         attachmentURL : attachment?.url  ?? null,
//       });

//       const newIncident = data.incident || data;


// //console.log('[REPORTINCIDENTPANEL-1] ', newIncident)


//       // build shared values once
//       const campusList = targetIDs
//         .map(id => buildingInfos.find(b => String(b.iBuildingID) === String(id))?.BuildingName)
//         .filter(Boolean)
//         .join(', ');

//       const isDrill = String(modeNow).toLowerCase() === 'drill';

//       // MESSAGE TEXT ---------------------------------------------------------
//       const messageBody = `This is a ${modeNow} ${incidentType} at ${campusList}`;
//       const finalMessageText = isDrill
//         ? `DRILL DRILL DRILL ${messageBody} DRILL DRILL DRILL`
//         : messageBody;

//       // VOICE TEXT -----------------------------------------------------------
//       const voiceBase = `This is a ${modeNow} SchoolShield ${incidentType} alert at ${campusList}.`;
//       const voiceSegment = isDrill
//         ? `DRILL DRILL DRILL ${voiceBase} DRILL`
//         : voiceBase;
//       const finalVoiceText = [voiceSegment, voiceSegment, voiceSegment].join('\n');

//       // DISPATCH -------------------------------------------------------------
//        axios.post('/ialert/dispatch', {
//         incidentID: newIncident.incidentID,
//         iAccountID: safeAcctID,
//         iBuildingIDs: targetIDs,
//         requestedByUserID: safeUID,
//         isSystemAdmin: isPrivileged,
//         incidentMode: modeNow,
//         silentMode: modeIsSilent ? 1 : 0,   // NEW
//         subject: `${modeNow} SchoolShield Alert – ${incidentType} *${modeNow}*`,
//         messageText: finalMessageText,
//         voiceText: finalVoiceText
//       }).catch(e => console.error('dispatch fail', e));

//       if (incidentType.toLowerCase().includes('test')) {
//         axios.post('/ialert/testmode/start', {
//           iAccountID: safeAcctID,
//           iCampusID: primaryCampusIdLocal,
//           iBuildingID: primaryCampusIdLocal,
//           startedByUserID: safeUID,
//           startedByFirstName: safeFName,
//           startedByLastName: safeLName,
//         }).catch(e => console.error('auto-start test-mode', e));
//       }
//     } catch (err) {
//       console.error('create incident', err);
//       alert('Server error');
//     } finally {
//       setIsPosting(false);
//       await loadIncidents();
//       setIncidentType(''); setIncidentCategory('');
//       setStatusArr([]);   setGroupsArr(['Staff Roster']);
//       setMessageText(''); setAttachment(null);
//       setShowSendModal(false);
//     }
//   };

//   /* ── local modal state  ── */
//   const [incidentType,setIncidentType] = useState('Lockdown');
//   const [incidentCategory,setIncidentCategory] = useState('');
//   const [statusArr,setStatusArr] = useState([]);
//   const [groupsArr,setGroupsArr] = useState(['Staff Roster']);
//   const [messageText,setMessageText] = useState('');
//   const [attachment,setAttachment] = useState(null);

//    const [showType,setShowType] = useState(false);
// const [showStatus,setShowStatus] = useState(false);
// const [showGroups,setShowGroups] = useState(false);
// const [showMsg,setShowMsg] = useState(false);
// const [showSendModal,setShowSendModal] = useState(false);
// const [showTypeCatModal, setShowTypeCatModal] = useState(false);

  
//   // 🆕 Flag to style the Type/Category modal only when launched by 911
//   const [isVerifyContext, setIsVerifyContext] = useState(false);

// // Force AlertTypeCategorySelector to remount each time the modal opens
// // (prevents intermittent defaulting when the selector uses internal state/defaultValue)
// const [typeCatModalKey, setTypeCatModalKey] = useState(0);

// // Open verify modal + guarantee deterministic default category when empty
// const openVerifyTypeCategoryModal = useCallback(() => {
//   const def = defaultCategoryForType(incidentType);
//   if (def) {
//     setIncidentCategory((prev) => (String(prev || '').trim() ? prev : def));
//   }
//   setIsVerifyContext(true);
//   setTypeCatModalKey((k) => k + 1);
//   setShowTypeCatModal(true);
// }, [incidentType]);





//   // NEW: Help modal state
//   const [showHelp, setShowHelp] = useState(false);

//   // Missing primary campus modal state
//   const [showMissingPrimaryCampus, setShowMissingPrimaryCampus] = useState(false);

// // NEW: dropdown tells the panel the user has no primary campus
// const handleMissingPrimaryCampusDetected = useCallback(() => {
//   setSelID('');
//   setSelName('');
//   setShowMissingPrimaryCampus(true);
// }, []);


//   // NEW: create incident, dispatch, and return the created record
//   const createIncidentAndDispatch = async () => {
//     const modeNow = modeIsDrill ? 'DRILL' : 'REAL';

//     const targetIDs = getAuthorizedTargetIDs();
//     if (!targetIDs.length) { alert('No authorized buildings selected.'); return null; }
//     const primaryCampusIdLocal = targetIDs[0];
//     const primaryCampusName =
//       (buildingInfos.find(b => String(b.iBuildingID) === String(primaryCampusIdLocal))?.BuildingName) ||
//       (user?.campusNames?.[0] ?? safeCampusName);

//     // 🆕 Uniform campus‑scoped duplicate gate
//     const dup = await findRecentIncidentByType(incidentType, primaryCampusIdLocal);
//     if (dup) {
//       return {
//         ...dup,
//         __existing: true,
//         __existingReason: 'active-duplicate',
//         __campusName: primaryCampusName,
//       };
//     }

//     try {
//       setIsPosting(true);
//       const { data } = await axios.post(API, {
//         iAccountID  : safeAcctID,
//         iCampusID   : primaryCampusIdLocal,
//         iBuildingID : primaryCampusIdLocal,
//         buildingName: primaryCampusName,
//         incidentType,
//         incidentCategory,
//         incidentMode : modeNow,
//         createdByUserID      : safeUID,
//         createdByName        : fullName,
//         createdByMobileNumber: user?.UserPhoneNumber || '',
//         iUserID     : safeUID,
//         userName    : user?.userName || user?.username || '',
//         myStatus      : statusArr,
//         sendToGroups  : groupsArr,
//         messageText   : messageText,
//         attachmentType: attachment?.type ?? null,
//         attachmentURL : attachment?.url  ?? null,
//       });

//       const newIncident = data.incident || data;

  
    
// const INCIDENT_ALERT_BASE_URL =
//   process.env.REACT_APP_INCIDENT_ALERT_BASE_URL || 'https://ialertu.cityshield.io/login';
  
// // Build a clickable deep-link for the SMS (includes incident + account for routing context)
// const incidentAlertLink = (() => {
//   const incidentID = String(newIncident.incidentID);
//   const iAccountID = String(safeAcctID);

//   try {
//     const url = new URL(INCIDENT_ALERT_BASE_URL);
//     url.searchParams.set('incidentID', incidentID);
//     url.searchParams.set('iAccountID', iAccountID);
//     return url.toString();
//   } catch (e) {
//     const sep = INCIDENT_ALERT_BASE_URL.includes('?') ? '&' : '?';
//     return (
//       `${INCIDENT_ALERT_BASE_URL}${sep}` +
//       `incidentID=${encodeURIComponent(incidentID)}` +
//       `&iAccountID=${encodeURIComponent(iAccountID)}`
//     );
//   }
// })();

//  axios.post('/ialert/dispatch', {
//   incidentID : newIncident.incidentID,
//   iAccountID : safeAcctID,
//   iBuildingIDs: targetIDs,
//   requestedByUserID: safeUID,
//   isSystemAdmin: isPrivileged,
//   incidentMode: modeNow,
//   silentMode: modeIsSilent ? 1 : 0,   // NEW
//   subject: `${modeNow} SchoolShield Alert – ${incidentType} *${modeNow}*`,

//   messageText: `This is a ${modeNow} ${incidentType} at ${
//     targetIDs
//       .map(id => buildingInfos.find(b => String(b.iBuildingID) === String(id))?.BuildingName)
//       .filter(Boolean).join(', ')
//   }\n\nView incident alert: ${incidentAlertLink}`,

//   voiceText: `This is a ${modeNow} ${incidentType} at ${
//     targetIDs
//       .map(id => buildingInfos.find(b => String(b.iBuildingID) === String(id))?.BuildingName)
//       .filter(Boolean).join(', ')
//   } emergency alert. Please check your device immediately.`
// }).catch(e => console.error('dispatch fail', e));


//       if ((incidentType || '').toLowerCase().includes('test')) {
//         axios.post('/ialert/testmode/start', {
//           iAccountID: safeAcctID,
//           iCampusID: primaryCampusIdLocal,
//           iBuildingID: primaryCampusIdLocal,
//           startedByUserID: safeUID,
//           startedByFirstName: safeFName,
//           startedByLastName : safeLName,
//         }).catch(e => console.error('auto-start test-mode', e));
//       }

//       return newIncident;
//     } finally {
//       setIsPosting(false);
//     }
//   };

//   const handleCategoryForModal = async (cat) => {
//     setIncidentCategory(cat);
//     if ((cat || '').trim().toLowerCase() === 'user selection') {
//       try {
//         const newIncident = await createIncidentAndDispatch();

//         if (newIncident?.__existing) {
//           await loadIncidents();
//           setPendingIncident(null);
//           setDeferOpen911(false);
//           setShowTypeCatModal(false);

//           const label  = canonicalTypeKey(incidentType);
//           const campus = newIncident.__campusName || safeCampusName;

//           alert(`An ACTIVE ${label} incident already exists for ${campus}. Opening it.`);
//           navigate(`/activeincidentpage/${newIncident.incidentID}`, { state: { incident: newIncident } });
//           return;
//         }

//         if (newIncident?.incidentID) {
//           await loadIncidents();
//           setPendingIncident(newIncident);
//           setDeferOpen911(true);
//         }
//       } catch (e) {
//         console.error('auto-create/open from selector', e);
//         alert('Failed to create the incident.');
//       } finally {
//         setStatusArr([]);   setGroupsArr(['Staff Roster']);
//         setMessageText(''); setAttachment(null);
//         setShowTypeCatModal(false);
//       }
//     }
//   };

//   /* ── role-gated visible list (client-side belt & suspenders) ── */
//   const visibleIncidents = React.useMemo(() => {
//     if (isAdmin) return incidents;
//     const pid = String(primaryCampusId || '');
//     return incidents.filter(x =>
//       String(x.iCampusID ?? '') === pid || String(x.iBuildingID ?? '') === pid
//     );
//   }, [incidents, isAdmin, primaryCampusId]);

//   // Map of buildingID -> BuildingName for quick lookup
//   const buildingNameMap = React.useMemo(() => {
//     const m = {};
//     (buildingInfos || []).forEach(b => { m[String(b.iBuildingID)] = b.BuildingName; });
//     return m;
//   }, [buildingInfos]);

 
//   /* ── incident row renderer (type + category + location pill + timestamp) ── */
// const rows = visibleIncidents.map(x => {
//   const varnt    = colour(x.incidentType);
//   const catLabel = x.incidentCategory ? `– ${x.incidentCategory}` : '';
//   const drill    = (x.incidentMode ?? '').toUpperCase() === 'DRILL';
//   const age      = ageFromNow(x.createdAt);
//   const ts       = fmtTS(x.createdAt);

//   // Resolve campus/primary location name robustly
//   const campusName =
//     x.buildingName ||
//     x.campusName ||
//     buildingNameMap[String(x.iBuildingID ?? '')] ||
//     buildingNameMap[String(x.iCampusID ?? '')] ||
//     '';

//   return (
//     <ListGroup.Item
//       key={x.incidentID}
//       className="p-1 mb-1"
//       role="button"
//       tabIndex={0}
//       onClick={() => {
//         sessionStorage.setItem(
//           'suppressAutoOpenUntil',
//           String(Date.now() + AUTO_SUPPRESS_MS)
//         );
//         navigate(`/activeincidentpage/${x.incidentID}`, { state: { incident: x } });
//       }}
//       onKeyDown={(e) => {
//         if (e.key === 'Enter' || e.key === ' ') {
//           e.preventDefault();
//           sessionStorage.setItem(
//             'suppressAutoOpenUntil',
//             String(Date.now() + AUTO_SUPPRESS_MS)
//           );
//           navigate(`/activeincidentpage/${x.incidentID}`, { state: { incident: x } });
//         }
//       }}
//       style={{
//         display: 'grid',
//         gridTemplateColumns: 'auto minmax(0,1fr) auto',
//         columnGap: 8,
//         alignItems: 'flex-start',
//         fontSize: 12,
//         fontWeight: 600,
//         background: x.isActive ? (x.color ?? '#ffeb99') : '#d3d3d3',
//         border: '2px solid #28a745',
//         borderRadius: 4,
//         cursor: 'pointer',
//         paddingTop: 4,
//         paddingBottom: 4,
//       }}
//     >
//       {/* Left: incident type pill */}
//       <div style={{ display: 'inline-flex', alignItems: 'center' }}>
//         <mark
//           className={`bg-${varnt} text-white px-1 rounded text-center`}
//           style={{ whiteSpace: 'nowrap', margin: 0 }}
//         >
//           {x.incidentType.toUpperCase()}
//         </mark>
//       </div>

//       {/* Middle: category text + location pill (AFTER the text, wraps if needed) */}
//       <div
//         style={{
//           display: 'flex',
//           flexWrap: 'wrap',
//           alignItems: 'center',
//           gap: 4,
//           lineHeight: 1.2,
//         }}
//       >
//         {/* category text – full, wrapping */}
//         {catLabel && (
//           <span
//             style={{
//               whiteSpace: 'normal',
//               wordBreak: 'break-word',
//             }}
//           >
//             {catLabel.toUpperCase()}
//           </span>
//         )}

//         {/* location pill – AFTER type/category, in normal flow */}
//         <span
//           title={campusName || 'Primary Campus'}
//           style={{
//             display: 'inline-flex',
//             alignItems: 'center',
//             padding: '0.1rem 0.5rem',
//             borderRadius: 9999,
//             background: 'rgba(255,255,255,0.9)',
//             border: '1px solid rgba(0,0,0,0.12)',
//             fontSize: 12,
//             fontWeight: 800,
//             lineHeight: 1.2,
//             color: '#212529',
//             whiteSpace: 'normal',      // allow wrapping
//             wordBreak: 'break-word',
//           }}
//         >
//           {campusName || 'Primary Campus'}
//         </span>

//         {drill && (
//           <span
//             style={{
//               padding: '0.05rem 0.35rem',
//               borderRadius: 9999,
//               background: 'rgba(0,128,0,0.12)',
//               border: '1px solid rgba(0,128,0,0.3)',
//               fontSize: 10,
//               fontWeight: 900,
//               textTransform: 'uppercase',
//               letterSpacing: '0.08em',
//               color: 'rgba(0,100,0,0.9)',
//             }}
//           >
//             Drill
//           </span>
//         )}
//       </div>

//       {/* Right: timestamp + 24h indicator (unchanged) */}
//       <div className="text-end" style={{ lineHeight: 1.1 }}>
//         <div className="small text-nowrap" title={x.createdAt || ''}>
//           {ts}
//         </div>
//         <div
//           className="small"
//           style={{
//             color: age.within24 ? '#198754' : '#6c757d',
//             fontWeight: 600,
//           }}
//           title={age.rel ? `${age.rel} ago` : ''}
//         >
//           {age.within24 ? '≤24h' : '>24h'}
//           {age.rel ? ` · ${age.rel}` : ''}
//         </div>
//       </div>
//     </ListGroup.Item>
//   );
// });






//   const handleUseSelection = async () => {
//     if (!incidentType) { alert('Incident Type is required.'); return; }
//     if (!incidentCategory) { alert('Pick a Category first.'); return; }

//     try {
//       const newIncident = await createIncidentAndDispatch();

//       if (newIncident?.__existing) {
//         await loadIncidents();
//         setPendingIncident(null);
//         setDeferOpen911(false);
//         setShowTypeCatModal(false);

//         const label  = canonicalTypeKey(incidentType);
//         const campus = newIncident.__campusName || safeCampusName;

//         alert(`An ACTIVE ${label} incident already exists for ${campus}. Opening it.`);
//         navigate(`/activeincidentpage/${newIncident.incidentID}`, { state: { incident: newIncident } });
//         return;
//       }

//       if (newIncident?.incidentID) {
//         await loadIncidents();
//         setPendingIncident(newIncident);
//         setDeferOpen911(true);
//       }
//     } catch (e) {
//       console.error('create/open from Next', e);
//       alert('Failed to create the incident.');
//     } finally {
//       setStatusArr([]);   setGroupsArr(['Staff Roster']);
//       setMessageText(''); setAttachment(null);
//       setShowTypeCatModal(false);
//     }
//   };


// const greenSwitchStyle = {
//   backgroundColor: 'rgba(80, 255, 160, 0.6)',
//   borderColor: 'rgba(80, 255, 160, 0.6)',
//   backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='white'/%3e%3c/svg%3e")`
// };

// const greenLabelStyle = {
//   color: 'black' // 0.6 = translucent
// };

//   /* ─── RENDER ─────────────────────────────────────────────── */

//   return (
//     <>
//       {/* Scoped CSS for verify context (911 launch) */}
//       <style>{`
//         .verify-alert-content { background-color:#ffe6e6 !important; border:3px solid #8b0000 !important; }
//         .verify-alert-content .modal-header, .verify-alert-content .modal-footer { border-color:#8b0000 !important; }
//         .verify-alert-content .modal-title { color:#8b0000 !important; font-weight:800 !important; }
//         /* Fallback: some RB versions only support dialogClassName */
//         .verify-alert-dialog .modal-content { background-color:#ffe6e6 !important; border:3px solid #8b0000 !important; }
//         .verify-alert-dialog .modal-header, .verify-alert-dialog .modal-footer { border-color:#8b0000 !important; }
//         .verify-alert-dialog .modal-title { color:#8b0000 !important; font-weight:800 !important; }
//       `}</style>

//       {/* Panel-scoped DRILL watermark appears ONLY after incident is created,
//           and ONLY for that pending incident during the 911 step. */}
//       {String(pendingIncident?.incidentMode || '').toUpperCase() === 'DRILL' && (
//         <div
//           aria-hidden="true"
//           style={{
//             position: 'fixed',
//             inset: 0,
//             zIndex: 1040,          // below Bootstrap modal (1050), above panel
//             pointerEvents: 'none',
//             display: 'flex',
//             alignItems: 'center',
//             justifyContent: 'center',
//             opacity: 0.1,
//             fontSize: '12vw',
//             fontWeight: 900,
//             color: 'green',
//             textTransform: 'uppercase',
//             userSelect: 'none',
//             transform: 'rotate(-30deg)',
//           }}
//         >
//           This&nbsp;is&nbsp;a&nbsp;Drill
//         </div>
//       )}

//       <Card style={{ width:'100vw',height:'100vh',
//                      border:'3px solid #f2c94c',borderRadius:0 }}>
//  <DashboardCardHeader
//   title={
//     <div className="d-flex align-items-center gap-2">
//       <span>Incident Alert</span>
//       <Button
//         variant="warning"
//         size="sm"
//         className="rounded-circle d-inline-flex align-items-center justify-content-center fw-bold"
//         style={{
//           width: 28,
//           height: 28,
//           lineHeight: 1,
//           backgroundColor: '#2d39daff',
//           color: '#fff',
//           border: '2px solid #fff',
//           boxShadow: '0 0 6px rgba(255,87,34,0.6)',
//           transition: 'all 0.2s ease-in-out'
//         }}
//         onMouseEnter={(e) => {
//           e.currentTarget.style.backgroundColor = '#ff5722';
//           e.currentTarget.style.borderColor = '#d32f2f';
//           e.currentTarget.style.boxShadow = '0 0 10px rgba(211,47,47,0.8)';
//         }}
//         onMouseLeave={(e) => {
//           e.currentTarget.style.backgroundColor = '#2d39daff';
//           e.currentTarget.style.borderColor = '#fff';
//           e.currentTarget.style.boxShadow = '0 0 6px rgba(255,87,34,0.6)';
//         }}
//         onClick={() => setShowHelp(true)}
//         aria-label="How to use the Report Incident panel"
//         title="How to use this panel"
//       >
//         ?
//       </Button>
//     </div>
//   }
//   onLogout={handleLogout}
//   closePath="/schoolshield"
//   hideDropdown
//   logoOverride={logo}
// />

//         <Card.Body className="d-flex flex-column pt-2 pb-3">
//           <Container fluid>
//             <div className={panelStyles.layoutShell}>

//               <Row className="mt-0 gy-3">
//                 {hasMultipleAlertCampuses && (
//                   <Alert variant="danger" className="mb-2" style={{ fontSize: '12px' }}>
//  You have more than one assigned campus. Select the correct campus before sending the alert.
//                   </Alert>
//                 )}

//               </Row>

//               <Row className="mt-0 gy-3">
//                 {/* Left: config card with selectors + 911 button */}
//                 <Col
//                   xs={12}
//                   md={7}
//                   lg={6}
//                   className={panelStyles.mainCol}
//                 >
 

//                   <div className={panelStyles.configCard}>
//                     {/* Campus selector row */}
//                     <div className={panelStyles.selectorRow}>
//                       <div className={panelStyles.selectorControl}>
//                         <div className={panelStyles.fieldHeaderRow}>
//                           <span className={panelStyles.fieldLabel}>
//                             Select Campus / Building
//                           </span>
//                         </div>
//  <DropdownSearchInfrastructureAlert
//   databaseContent={assignedCampusBuildingInfos}
//   selectedID={selID}
//   onSelect={handleSelectCampus}
//   onMissingPrimaryCampus={handleMissingPrimaryCampusDetected}
// />
//                       </div>
//                     </div>

//                     {/* Incident type row */}
//                     <div className={panelStyles.selectorRow}>
//                       <div className={panelStyles.selectorControl}>
//                         <div className={panelStyles.fieldHeaderRow}>
//                           <span className={panelStyles.fieldLabel}>
//                             Select Incident Type
//                           </span>
//                         </div>
//                         <Button
//                           variant="outline-primary"
//                           className={panelStyles.selectorButton}
//                           onClick={() => setShowType(true)}
//                         >
//                           {incidentType
//                             ? `${incidentType}${
//                                 incidentCategory
//                                   ? ' – ' + incidentCategory
//                                   : ''
//                               }`
//                             : 'Select Incident Type'}
//                         </Button>
//                       </div>
//                     </div>

//                     <div
//   style={{
//     display: 'grid',
//     gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
//     gap: 8,
//     marginTop: 10,
//   }}
// >
 

 
// </div>

//                     {/* 911 button */}
//                     <div className={panelStyles.emergencyButtonWrapper}>
//                       <Button
//                         variant="danger"
//                         className={panelStyles.emergencyButton}
//                         onClick={openVerifyTypeCategoryModal}
//                       >
//                         <ExclamationTriangleFill
//                           size={32}
//                           className="me-2"
//                         />
//                         <span className={panelStyles.emergencyText}>
//                           Report {incidentType} Incident and Dial 911
//                         </span>
//                       </Button>
//                     </div>
//                   </div>
//                 </Col>

//                 {/* Right: admin tools card */}
//                 <Col
//                   xs={12}
//                   md={5}
//                   lg={4}
//                   className={panelStyles.adminCol}
//                 >
//                   {isAdmin && (
//                     <div className={panelStyles.adminCard}>
//                       <div className={panelStyles.adminHeader}>
//                         Administrator Tools
//                       </div>
//                       <div className={panelStyles.adminBody}>
//                         <div className={panelStyles.adminButtonRow}>
//                           <Button
//                             size="sm"
//                             variant={
//                               effectiveShowAll
//                                 ? 'outline-primary'
//                                 : 'secondary'
//                             }
//                             onClick={() => setShowAll((p) => !p)}
//                           >
//                             {effectiveShowAll
//                               ? 'Show Last 24h'
//                               : 'Show All Incidents'}
//                           </Button>

//                           <Button
//                             size="sm"
//                             variant="outline-primary"
//                             onClick={() =>
//                               navigate('/dashboard', {
//                                 state: {
//                                   iCampusID: '-99',
//                                   iBuildingID: '-99',
//                                   activeOnly: effectiveShowAll ? '0' : '1',
//                                 },
//                               })
//                             }
//                           >
//                             Incident Monitor
//                           </Button>
//                         </div>

//                                        <div
//                           className={panelStyles.adminToggleRow}
//                           style={{
//                             display: 'flex',
//                             gap: '1.25rem',
//                             flexWrap: 'wrap',
//                             alignItems: 'center'
//                           }}
//                         >



//                           {/* <Form.Check
//                             type="switch"
//                             id="toggle-drill-mode"
//                             checked={modeIsDrill}
//                             onChange={(e) => {
//                               const v = e.target.checked;
//                               setModeIsDrill(v);
//                               sessionStorage.setItem(
//                                 'panelModeIsDrill',
//                                 v ? '1' : '0'
//                               );
//                               if (v) {
//                                 localStorage.setItem('isTestIncidentOverride', '1');
//                               } else {
//                                 localStorage.removeItem('isTestIncidentOverride');
//                               }
//                             }}
//                             label={
//                               modeIsDrill
//                                 ? 'DRILL (test mode)'
//                                 : 'REAL (live)'
//                             }
//                             style={{
//                               fontSize: '0.85rem',
//                               display: 'flex',
//                               alignItems: 'center',
//                               fontWeight: '700'
//                             }}
//                             className="align-items-center"
//                           />

//                           <Form.Check
//                             type="switch"
//                             id="toggle-silent-mode"
//                             checked={modeIsSilent}
//                             onChange={(e) => setModeIsSilent(e.target.checked)}
//                             label={
//                               modeIsSilent
//                                 ? 'SILENT (no SMS/voice/email)'
//                                 : 'LOUD (send SMS/voice/email)'
//                             }
//                             style={{
//                               fontSize: '0.85rem',
//                               display: 'flex',
//                               alignItems: 'center',
//                               fontWeight: '700'
//                             }}
//                             className="align-items-center"
//                           /> */}

// <div
//   className="form-check form-switch align-items-center mb-0"
//   style={{
//     fontSize: '0.85rem',
//     display: 'flex',
//     alignItems: 'center',
//     fontWeight: '700',
//     gap:'5px'
//   }}
// >
//   <input
//     className="form-check-input"
//     type="checkbox"
//     role="switch"
//     id="toggle-drill-mode"
//     checked={modeIsDrill}
//     onChange={(e) => {
//       const v = e.target.checked;
//       setModeIsDrill(v);
//       sessionStorage.setItem('panelModeIsDrill', v ? '1' : '0');

//       if (v) {
//         localStorage.setItem('isTestIncidentOverride', '1');
//       } else {
//         localStorage.removeItem('isTestIncidentOverride');
//       }
//     }}
//     style={!modeIsDrill ? greenSwitchStyle : undefined}
//   />
//   <label
//     className="form-check-label"
//     htmlFor="toggle-drill-mode"
//     style={!modeIsDrill ? greenLabelStyle : undefined}
//   >
//     {modeIsDrill ? 'DRILL (test mode)' : 'REAL (live)'}
//   </label>
// </div>

// <div
//   className="form-check form-switch align-items-center mb-0"
//   style={{
//     fontSize: '0.85rem',
//     display: 'flex',
//     alignItems: 'center',
//     fontWeight: '700',
//     gap:'5px'
//   }}
// >
//   <input
//     className="form-check-input"
//     type="checkbox"
//     role="switch"
//     id="toggle-silent-mode"
//     checked={modeIsSilent}
//     onChange={(e) => setModeIsSilent(e.target.checked)}
//     style={!modeIsSilent ? greenSwitchStyle : undefined}
//   />
  
//   <label
//     className="form-check-label"
//     htmlFor="toggle-silent-mode"
//     style={!modeIsSilent ? greenLabelStyle : undefined}
//   >
   
//     {modeIsSilent
//       ? 'SILENT (no SMS/voice/email)'
//       : 'LOUD (send SMS/voice/email)'}
//   </label>
// </div>






//                         </div>


//                       </div>
//                     </div>
//                   )}
//                 </Col>
//               </Row>

//               {visibleIncidents.length > 0 && (
//                 <>
//                   <hr className="p-1" />
//                   <Row className="align-items-center mb-1">
//                     <Col>
//                       <h6 className="text-center m-0">
//                         {effectiveShowAll
//                           ? 'All Incidents (select row to open)'
//                           : 'Incidents – Last 24 Hours (select row to open)'}
//                       </h6>
//                     </Col>
//                   </Row>

//                   <Row style={{ overflow: 'hidden' }}>
//                     <div
//                       style={{
//                         maxHeight: '35vh',
//                         overflowY: 'auto',
//                         width: '100%',
//                         paddingRight: 2,
//                       }}
//                     >
//                       <ListGroup>{rows}</ListGroup>
//                     </div>
//                   </Row>
//                 </>
//               )}
//             </div>
//           </Container>
//         </Card.Body>

//       </Card>

//       {/* modal components – logic unchanged */}
//       <IncidentTypeModal show={showType} onHide={()=>setShowType(false)}
//         onSave={({incidentType,category})=>{
//           setIncidentType(incidentType); setIncidentCategory(category);
//         }}/>

//  <ReportStatusModal
//   show={showStatus}
//   onHide={() => setShowStatus(false)}
//   onSave={setStatusArr}
//   initial={statusArr}
//   specialStudentScope={specialStudentScope}
//   user={user}
//   actorName={fullName}
// />
 



//       <GroupsModal show={showGroups} onHide={()=>setShowGroups(false)}
//         onSave={setGroupsArr} initial={groupsArr}/>
//       <MessageMediaModal show={showMsg} onHide={()=>setShowMsg(false)}
//         onSave={({text,attachment})=>{
//           setMessageText(text); setAttachment(attachment);
//         }}/>
//       <SendStatusModal show={showSendModal} onHide={()=>setShowSendModal(false)}
//         onSend={sendAlert} summary={{
//           fullName, location:safeCName, statusArr,
//           groupsArr, messageText, attachment
//         }}
//         openLocation={()=>{setShowSendModal(false);setShowGroups(false);}}
//         openStatus  ={()=>{setShowSendModal(false);setShowStatus(true );}}
//         openGroups  ={()=>{setShowSendModal(false);setShowGroups(true );}}
//         openMessage ={()=>{setShowSendModal(false);setShowMsg(true   );}}
//       />

//       {/* Help modal */}
//       <Modal show={showHelp} onHide={() => setShowHelp(false)} centered scrollable>
//         <Modal.Header closeButton>
//           <Modal.Title>How to use the Report Incident panel</Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           <ol className="mb-0 ps-3">
//             <li>Set <b>Campus</b> and <b>Incident Type/Category</b> (defaults usually OK).</li>
//                         <li>Admins can toggle <b>DRILL/REAL</b> and <b>SILENT/LOUD</b>. Silent suppresses SMS, voice, and email only; incident creation, DB activity, and webpush still continue.</li>
//             <li><b>Next</b> creates & opens the incident; messages fan out.</li>
//             <li>If a duplicate is blocked, it’s because an <b>ACTIVE same‑type</b> incident already exists in the <b>last 24h</b>. Closed incidents in the last 24h do <i>not</i> block.</li>
//             <li>Select any row below to open its incident.</li>
//           </ol>
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant="primary" onClick={() => setShowHelp(false)}>Close</Button>
//         </Modal.Footer>
//       </Modal>

//       {/* Type/Category modal (Type locked). Auto-creates on "User Selection"
//           NOTE: Styled + retitled ONLY when launched via 911 button */}
//       <Modal
//         show={showTypeCatModal}
//         onHide={() => { setShowTypeCatModal(false); setIsVerifyContext(false); }}
//         centered
//         onExited={() => {
//           if (deferOpen911 && pendingIncident) {
//             setDeferOpen911(false);
//             open911Modal(pendingIncident);
//           }
//           setIsVerifyContext(false); // reset after close
//         }}
//         dialogClassName={isVerifyContext ? 'verify-alert-dialog' : undefined}
//         contentClassName={isVerifyContext ? 'verify-alert-content' : undefined}
//       >
//         <Modal.Header closeButton>
//           <Modal.Title>
//             {isVerifyContext
//               ? 'VERIFY INCIDENT TYPE AND CATEGORY BEFORE SENDING ALERT'
//               : 'Select Category'}
//           </Modal.Title>
//         </Modal.Header>

//         <Modal.Body>
//           <AlertTypeCategorySelector
//             key={typeCatModalKey}
//             selectedType={incidentType}
//             selectedCategory={incidentCategory}
//             onTypeChange={setIncidentType}
//             onCategoryChange={handleCategoryForModal}
//             disableType={true}
//           />
//           <div className="mt-2 small text-muted">
//             Incident Type is locked to <b>{incidentType || 'Lockdown'}</b>. Pick a Category.
//             Choosing <b>User Selection</b> will immediately create and open the incident.
//           </div>
//         </Modal.Body>

//         <Modal.Footer>
//           <Button className="me-auto" variant="secondary" onClick={() => { setShowTypeCatModal(false); setIsVerifyContext(false); }}>
//             Cancel
//           </Button>
//           <Button
//             variant="primary"
//             onClick={handleUseSelection}
//             disabled={!incidentCategory || isPosting}
//           >
//             {isPosting ? <Spinner animation="border" size="sm" className="me-2" /> : null}
//             Next
//           </Button>
//         </Modal.Footer>
//       </Modal>


//       <NoPrimaryCampusModal
//         show={showMissingPrimaryCampus}
//         onClose={handleMissingPrimaryCampusClose}
//       />

//       {/* 911: swipe-to-call modal */}
//       <Modal
//         show={showCall911}
//         onHide={() => {}}
//         backdrop="static"
//         keyboard={false}
//         centered
//       >
//         <Modal.Header>
//           <Modal.Title>Dial 911?</Modal.Title>
//         </Modal.Header>

//         <Modal.Body>
//           <div className="mb-2">
//             Do you wish to dial <b>911</b> now?
//           </div>
//           <div className="text-muted small mb-3">
//             Swipe right to call 911, or select Send Now to continue without calling.
//           </div>

//           <div className="px-1">
//             <Form.Range
//               value={dialProgress}
//               min={0}
//               max={100}
//               step={1}
//               onChange={(e) => setDialProgress(Number(e.target.value))}
//               onMouseUp={handleSliderRelease}
//               onTouchEnd={handleSliderRelease}
//             />
//             <div className="text-center fw-semibold">
//               {dialProgress >= 95 ? 'Release to call' : 'Swipe right to call 911'}
//             </div>
//           </div>
//         </Modal.Body>

//         <Modal.Footer>
//           <Button variant="secondary" onClick={navigateToPendingIncident}>
//             Send Alert Now!
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   );
// }

 